import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Box,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Snackbar,
  Autocomplete,
  LinearProgress,
  TableSortLabel,
  Collapse,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningIcon from '@mui/icons-material/Warning';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ViewListIcon from '@mui/icons-material/ViewList';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import TodayIcon from '@mui/icons-material/Today';
// BlurOnIcon removed - no longer needed after OBD category splitting
import PauseIcon from '@mui/icons-material/Pause';
import GroupIcon from '@mui/icons-material/Group';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pulseRequest } from '../utils/pulseRequest';
import ClarisLogo from '../assets/claris_logo_transparent[67291746].png';

// Standards-feedback display helpers (advisory dialog text only)
const fmtDur = (seconds) => {
  const m = Math.round(seconds / 60);
  if (m < 1) return 'under a minute';
  const h = Math.floor(m / 60);
  return h > 0 ? `${h} h ${m % 60} min` : `${m} min`;
};
// Always Eastern, always labeled: the explicit timeZone means the shown clock
// time is ET no matter what the device or server is set to, and the "ET"
// suffix keeps a viewer in another timezone from reading it as local time.
const doneBy = (seconds) =>
  `${new Date(Date.now() + seconds * 1000).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} ET`;

// ==================== TOTE ESTIMATE (display-only) ====================
// Commented MIRROR of backend/lib/totes.js — keep the two in sync:
//   department = mapping[CATEGORY] else 'GenMerch' (any unmapped category);
//   totes = max(1, ceil(units / units_per_tote)) — never fractional;
//   batches sum the per-OBD estimates;
//   cart line ONLY when totes_per_cart is set AND the totes exceed it;
//   a department with estimate_by_department[dept] === false shows NOTHING
//   (no estimate, no cart line — e.g. Beverages; absent flag = enabled).
// Gated by tote_estimate_enabled alone (independent of standards feedback
// and auto-assignment). tc = { capacities, mapping, totes_per_cart,
// estimate_by_department }.
const toteEstimateFor = (units, category, tc) => {
  if (!tc || !tc.capacities) return null;
  const dept = (tc.mapping && tc.mapping[String(category || '').trim().toUpperCase()]) || 'GenMerch';
  if (tc.estimate_by_department && tc.estimate_by_department[dept] === false) return null;
  const upt = Number(tc.capacities[dept]);
  const u = Number(units);
  if (!Number.isFinite(upt) || upt <= 0 || !Number.isFinite(u) || u < 0) return null;
  return Math.max(1, Math.ceil(u / upt));
};
const toteBatchTotal = (rows, tc) => {
  let sum = 0;
  for (const r of rows || []) {
    const t = toteEstimateFor(r.units, r.category, tc);
    if (t) sum += t;
  }
  return sum || null;
};
const toteCartsNeeded = (totes, tc) => {
  const tpc = Number(tc && tc.totes_per_cart);
  if (!Number.isFinite(tpc) || tpc <= 0 || !totes || totes <= tpc) return null;
  return Math.ceil(totes / tpc);
};
const ToteIcon = ({ size = 22, color = '#344054' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M3 6.5h18l-1.9 12.3a1.8 1.8 0 0 1-1.78 1.5H6.68a1.8 1.8 0 0 1-1.78-1.5L3 6.5z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
    <path d="M2 6.5h20" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
    <path d="M8.2 10.2h2.6M13.2 10.2h2.6" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
// "About N totes" line + gray Estimate label + optional amber cart line.
const ToteEstimate = ({ totes, carts, fontSize = 18, iconSize = 22, align = 'flex-start', mt = 1 }) => {
  if (!totes) return null;
  return (
    <div style={{ marginTop: 8 * mt }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: align, gap: Math.round(iconSize * 0.4) }}>
        <ToteIcon size={iconSize} />
        <span style={{ fontSize, fontWeight: 600, color: '#344054' }}>
          About {totes} tote{totes === 1 ? '' : 's'}
        </span>
        <span style={{ fontSize: Math.max(12, Math.round(fontSize * 0.62)), color: '#98A2B3' }}>Estimate</span>
      </div>
      {carts != null && (
        <div style={{ fontSize: Math.max(13, Math.round(fontSize * 0.78)), color: '#B54708', fontWeight: 600, textAlign: align === 'center' ? 'center' : 'left' }}>
          Likely needs {carts} carts
        </div>
      )}
    </div>
  );
};


const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

// Category colors for badges
const CATEGORY_COLORS = {
  // New split categories
  'CANDY': '#e91e63',        // Pink - stands out for candy
  'SNACKS': '#ff9800',       // Orange - for non-candy convenience food
  'BEVERAGES': '#2196f3',    // Blue - for beverages
  // Original categories (some may still appear)
  'CONVENIENCE FOOD': '#ff9800',  // Legacy - now split into CANDY/SNACKS
  'NON ALCOHOLIC BEV': '#2196f3', // Legacy - now BEVERAGES
  'HBA': '#4caf50',
  'ELECTRONICS': '#9c27b0',
  'PREMIUM ELECTRONICS': '#7b1fa2',
  'DRINKWARE': '#00bcd4',
  'TRAVEL': '#795548',       // Renamed from PILLOWS/BLANKETS
  'PILLOWS/BLANKETS': '#795548',  // Legacy
  'TOYS': '#f06292',
  'GIFTS': '#f44336',
  'APPAREL': '#3f51b5',
  'SPORTS': '#558b2f',
  'EYEWEAR': '#00838f',
  'UNKNOWN': '#9e9e9e'
};

// Age band colors for the OBD Sign-On "Age" column Chip.
// Source: obds.sap_created_on (SAP CREATEDON, YYYYMMDD persisted as DATE).
// NULL → no Chip rendered (empty cell), never defaulted to "Today".
const AGE_BAND_COLORS = {
  today:     '#43a047', // green  — today's business day
  aged1:     '#fbc02d', // yellow — 1 day old
  aged2plus: '#c62828', // red    — 2+ days old (strong red; row-bg urgency is separate)
};

// Returns 'today' | 'aged1' | 'aged2plus' | null.
// NULL when sap_created_on is missing (legacy rows + manual Fiori imports) —
// caller renders no Chip in that case.
// Uses the 3:30 AM Eastern business-day cutoff, matching backend
// lib/businessDay.js (keep in sync — the whole app shares one definition).
const getAgeBand = (sapCreatedOn) => {
  if (!sapCreatedOn) return null;
  const iso = String(sapCreatedOn).slice(0, 10); // accepts 'YYYY-MM-DD' or Date-stringified
  const [cy, cm, cd] = iso.split('-').map(Number);
  if (!cy || !cm || !cd) return null;
  const createdLocal = new Date(cy, cm - 1, cd); // local-zone midnight (no UTC drift)

  const now = new Date();
  const [etH, etM] = now
    .toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit' })
    .split(':').map((v) => parseInt(v, 10));
  const etDateStr = now.toLocaleDateString('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit'
  });
  const [m, d, y] = etDateStr.split('/');
  const todayLocal = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  if ((etH % 24) * 60 + etM < 210) todayLocal.setDate(todayLocal.getDate() - 1); // pre-3:30am rolls back to prior business day

  const dayDiff = Math.round((todayLocal - createdLocal) / 86400000);
  if (dayDiff <= 0) return 'today';
  if (dayDiff === 1) return 'aged1';
  return 'aged2plus';
};

const AGE_BAND_LABELS = {
  today:     'Today',
  aged1:     '1d',
  aged2plus: '2d+',
};

// Platform colors
const PLATFORM_COLORS = {
  'ATL': '#1976d2',
  'ATL A&B': '#1976d2',      // Same blue for ATL A&B
  'ATL E/T/ATR': '#0d47a1',  // Darker blue for ATL E/T/ATR
  'MEM': '#388e3c',
  'BNA': '#d32f2f',
  'CAE': '#7b1fa2',
  'GSO': '#f57c00',
  'PNS': '#0097a7',
  'RDU': '#c2185b',
  'SAV': '#689f38',
  'TYS': '#455a64',
  'ASH': '#5d4037',
  'UNKNOWN': '#9e9e9e'
};

// Store priority order (lower number = higher priority)
// Used for default sort: stores sorted by priority, then by line count within each store
const STORE_PRIORITY = {
  // ATL E-Con (Beverages, Candy, Snacks - TOP PRIORITY)
  '2018': 1,
  '2033': 2,
  '2036': 3,

  // ATL A-Con
  '2028': 4,
  '2029': 5,

  // ATL B-Con
  '2032': 6,
  '2453': 7,

  // ATL Atrium
  '2021': 8,

  // ATL T-Con
  '6049': 9,

  // Additional Atlanta stores
  '2030': 10,
  '2031': 11,
  '2037': 12,

  // Memphis
  '1211': 13,
  '1213': 14,
  '1214': 15,
  '1208': 16,
  '1215': 17,
  '1210': 18,

  // Raleigh
  '676': 19,
  '673': 20,

  // Greensboro
  '642': 21,
  '643': 22,
  '641': 23,

  // Columbia
  '233': 24,
  '230': 25,

  // Nashville
  '157': 26,
  '160': 27,
  '161': 28
};

// Fallback priority for stores not in list
const DEFAULT_STORE_PRIORITY = 999;

// Business day helper — 3:30 AM ET cutoff, matching backend lib/businessDay.js
// (keep in sync — the whole app shares one definition).
// Work done at 3:15am on 01/21 → business day = 01/20 (before the cutoff)
// Work done at 3:45am on 01/21 → business day = 01/21
const getBusinessDay = (timestamp = new Date()) => {
  const date = new Date(timestamp);
  const [etH, etM] = date
    .toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit' })
    .split(':').map((v) => parseInt(v, 10));
  const etDateStr = date.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const [month, day, year] = etDateStr.split('/');
  let businessDate = new Date(year, month - 1, day);
  if ((etH % 24) * 60 + etM < 210) {
    businessDate.setDate(businessDate.getDate() - 1);
  }
  return businessDate.toISOString().split('T')[0];
};

// Helper to create a date at a specific time (assumes local machine is in Eastern timezone or similar)
const createEasternTime = (date, hours, minutes = 0) => {
  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);
  return d;
};

// Get today's default shift times (3:00 PM to 11:00 PM — night shift since Nov 2 2026) — mirrors Reports.js
const getDefaultShiftTimes = () => {
  const today = new Date();
  const startTime = createEasternTime(today, 18, 0); // 6:00 PM
  const endTime = createEasternTime(today, 2, 0); // 2:00 AM
  endTime.setDate(endTime.getDate() + 1); // Next day
  return { startTime, endTime };
};

const OBDSignOn = () => {
  // Auth context - get user role (manager and admin can access Manager View)
  const { currentUser, canAccessManagerFeatures, isAssociate } = useAuth();
  const navigate = useNavigate();

  // 401 from any authenticated request → the login session has expired.
  // Full-screen re-login state (same pattern as the Activity Monitor kiosk)
  // instead of raw error snackbars.
  const [authExpired, setAuthExpired] = useState(false);

  // State
  const [activeTab, setActiveTab] = useState(0); // 0 = Associate View, 1 = Manager View

  // OBD data
  const [obds, setObds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Workers
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [newWorkerName, setNewWorkerName] = useState('');
  const [newWorkerEmployeeId, setNewWorkerEmployeeId] = useState('');
  const [newWorkerError, setNewWorkerError] = useState('');
  const [addWorkerDialogOpen, setAddWorkerDialogOpen] = useState(false);

  // Import
  const [importData, setImportData] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  // Labor Assignment
  const [ftAssociates, setFtAssociates] = useState(0);
  const [ptAssociates, setPtAssociates] = useState(0);
  const [shiftStartTime, setShiftStartTime] = useState('15:00');
  const [laborAnalysis, setLaborAnalysis] = useState(null);
  const [laborDialogOpen, setLaborDialogOpen] = useState(false);
  const [laborAnalysisDialogOpen, setLaborAnalysisDialogOpen] = useState(false); // Separate state for import result dialog
  const [currentLabor, setCurrentLabor] = useState(null);
  const [platformCompletion, setPlatformCompletion] = useState(null);
  const [showPlatformCompletion, setShowPlatformCompletion] = useState(false);

  // Filters - all are multi-select arrays now
  const [platformFilter, setPlatformFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [shipToFilter, setShipToFilter] = useState([]);
  // Exclude modes for each filter (true = exclude selected, false = include selected)
  const [platformExclude, setPlatformExclude] = useState(false);
  const [statusExclude, setStatusExclude] = useState(false);
  const [categoryExclude, setCategoryExclude] = useState(false);
  const [shipToExclude, setShipToExclude] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [obdSearch, setObdSearch] = useState(''); // OBD number search

  // Sorting
  const [sortColumn, setSortColumn] = useState('default'); // 'default' uses store priority sort
  const [sortDirection, setSortDirection] = useState('asc');

  // Show/hide completed
  const [showCompleted, setShowCompleted] = useState(true);

  // Summary stats
  const [summary, setSummary] = useState(null);

  // Timer for live updates
  const [currentTime, setCurrentTime] = useState(new Date());

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // OBD Detail Modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [obdDetails, setObdDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Historical View — date range, mirrors Reports.js DateTimePicker pattern
  const [isHistoricalView, setIsHistoricalView] = useState(false);
  const _defaultHistoricalTimes = getDefaultShiftTimes();
  const [historicalStart, setHistoricalStart] = useState(_defaultHistoricalTimes.startTime);
  const [historicalEnd, setHistoricalEnd] = useState(_defaultHistoricalTimes.endTime);
  const [historicalStats, setHistoricalStats] = useState(null);

  // Cross-history OBD search (active only when isHistoricalView is ON)
  const [historySearchResults, setHistorySearchResults] = useState(null); // null = not in search mode
  const [historySearchLoading, setHistorySearchLoading] = useState(false);

  // Associate Report Modal
  const [associateModalOpen, setAssociateModalOpen] = useState(false);
  const [associateStats, setAssociateStats] = useState(null);
  const [associateReportPeriod, setAssociateReportPeriod] = useState('day');
  const [loadingAssociateStats, setLoadingAssociateStats] = useState(false);
  const [selectedAssociateForReport, setSelectedAssociateForReport] = useState(null);

  // Multi-select for batch assignment (available to all users in Associate View)
  const [selectedOBDs, setSelectedOBDs] = useState([]);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignTargetObd, setReassignTargetObd] = useState(null);
  const [preassignDialogOpen, setPreassignDialogOpen] = useState(false);
  const [preassignTargetObd, setPreassignTargetObd] = useState(null);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);

  // Legacy state for manager features (keep for now)
  const selectedAssignments = selectedOBDs; // Alias for backwards compatibility
  const setSelectedAssignments = setSelectedOBDs;

  // Edit Labor Plan dialog state
  const [editFtAssociates, setEditFtAssociates] = useState(0);
  const [editPtAssociates, setEditPtAssociates] = useState(0);
  const [editShiftStartTime, setEditShiftStartTime] = useState('15:00');

  // Associate View Settings (manager feature)
  const [setViewDialogOpen, setSetViewDialogOpen] = useState(false);
  const [viewAsDialogOpen, setViewAsDialogOpen] = useState(false);
  const [associatesList, setAssociatesList] = useState([]);
  const [viewingAsAssociate, setViewingAsAssociate] = useState(null); // When manager is viewing as associate
  const [allViewSettings, setAllViewSettings] = useState([]); // All saved view settings
  const [resetViewsDialogOpen, setResetViewsDialogOpen] = useState(false);
  const [viewSettingsDetailOpen, setViewSettingsDetailOpen] = useState(false);

  // Employee ID verification modal state (for associate accounts only)
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyEmployeeId, setVerifyEmployeeId] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { type: 'claim'|'complete'|'pause'|'resume'|'multiClaim', obdId?, obdIds? }
  // Standards feedback (admin toggle standards_feedback_enabled, default OFF).
  // Advisory only — every lookup fails open to "show nothing".
  const [standardsFeedbackEnabled, setStandardsFeedbackEnabled] = useState(false);
  // Lead-controlled auto-assignment (config toggle, default OFF). With it off,
  // this page behaves exactly as before.
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  const [nextObdDialog, setNextObdDialog] = useState(null); // { obd?, message?, empId, expectedSeconds?, verdict? }
  const [feedbackThreshold, setFeedbackThreshold] = useState(100); // completion-popup tier cut
  // Associate kiosk (auto mode only): name grid → verify → my-OBD screen.
  const [kioskWorker, setKioskWorker] = useState(null); // verified { employee_id, name }
  const [kioskRoster, setKioskRoster] = useState([]);   // clocked-in associates
  const [kioskSearch, setKioskSearch] = useState('');
  const [kioskGroup, setKioskGroup] = useState(null);   // { name, categories, is_general }
  const [kioskNow, setKioskNow] = useState(Date.now());
  const kioskIdleRef = useRef(null);
  const [verifyFeedback, setVerifyFeedback] = useState(null); // { mode:'start'|'complete', ... }
  // Tote estimate (admin toggle tote_estimate_enabled, default OFF). null =
  // off or unavailable → no tote UI anywhere. Display-only, fails open.
  const [toteConfig, setToteConfig] = useState(null); // { capacities, mapping, totes_per_cart }

  // Check-in SOFT gate (Change 6): a dismissible nag — NEVER blocks a pick.
  // Fires only when a not-checked-in associate starts an OBD (they identify
  // themselves by grabbing one), so it's targeted at the right person. No
  // recurring timer: on a shared tablet an interval is tied to the device,
  // not the person, and would pester whoever walks up next.
  // nagEnabled is an admin pilot toggle (default off).
  const [nagEnabled, setNagEnabled] = useState(false);
  const [clockNag, setClockNag] = useState({ open: false, message: '' });
  // Auto-Picking corrective warning: shown when ensure-picking had to FIX the
  // associate's state (checked out or on break at claim time). Dismissible,
  // never blocks — the pick already succeeded. Legitimate switches get a toast.
  const [autoPickWarn, setAutoPickWarn] = useState({ open: false, message: '' });

  // Reset OBD dialog state (manager/admin only)
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTargetObd, setResetTargetObd] = useState(null);

  // Bulk delete dialog state (manager/admin only)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // Platforms list - ATL is split into sub-platforms for different departure times
  const platforms = ['ASH', 'ATL A&B', 'ATL E/T/ATR', 'BNA', 'CAE', 'GSO', 'MEM', 'PNS', 'RDU', 'SAV', 'TYS'];

  // Get unique categories from OBDs
  const uniqueCategories = useMemo(() => {
    const cats = new Set(obds.map(o => o.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [obds]);

  // Get unique ship-to codes from OBDs
  const uniqueShipTos = useMemo(() => {
    const shipTos = new Set(obds.map(o => o.ship_to_code).filter(Boolean));
    return Array.from(shipTos).sort();
  }, [obds]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (platformFilter.length > 0) count++;
    if (statusFilter.length > 0) count++;
    if (categoryFilter.length > 0) count++;
    if (shipToFilter.length > 0) count++;
    if (obdSearch) count++;
    return count;
  }, [platformFilter, statusFilter, categoryFilter, shipToFilter, obdSearch]);

  // Fetch workers (endpoint requires auth — the roster is PII and employee
  // IDs are the kiosk credential; every user of this page is logged in)
  const fetchWorkers = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    const r = await pulseRequest(`${API_BASE}/api/obd/workers`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (r.sessionExpired) { setAuthExpired(true); return; }
    if (r.ok && Array.isArray(r.data)) setWorkers(r.data);
    else if (!r.ok) console.error('Error fetching workers:', r.message);
  }, []);

  // Fetch all OBDs (including completed) with retry logic for slow connections
  const fetchAllOBDs = useCallback(async () => {
    setLoading(true);
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds
    const timeout = 20000; // 20 seconds

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const allToken = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE}/api/obd/all`, {
          signal: controller.signal,
          headers: allToken ? { Authorization: `Bearer ${allToken}` } : {}
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          setObds(data);
          setError(null);
          setLoading(false);
          return; // Success - exit the retry loop
        } else {
          throw new Error('Failed to fetch OBDs');
        }
      } catch (err) {
        const isLastAttempt = attempt === maxRetries;
        const isTimeout = err.name === 'AbortError';

        console.error(`Error fetching OBDs (attempt ${attempt}/${maxRetries}):`,
          isTimeout ? 'Request timed out' : err.message);

        if (isLastAttempt) {
          setError('Failed to load OBDs. Is the backend running?');
          setLoading(false);
          return;
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }, []);

  // Fetch summary stats
  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/obd/stats/summary`);
      if (response.ok) {
        const data = await response.json();
        console.log('📊 OBD Summary stats:', data);
        setSummary(data);
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  }, []);

  // Fetch current day's labor assignment data
  const fetchCurrentLabor = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/obd/imports/latest`);
      if (response.ok) {
        const data = await response.json();
        setCurrentLabor(data);
      } else {
        setCurrentLabor(null);
      }
    } catch (error) {
      console.error('Error fetching labor data:', error);
      setCurrentLabor(null);
    }
  }, []);

  // Fetch completion estimates by platform
  const fetchPlatformCompletion = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/obd/completion-by-platform`);
      if (response.ok) {
        const data = await response.json();
        setPlatformCompletion(data);
      } else {
        setPlatformCompletion(null);
      }
    } catch (error) {
      console.error('Error fetching platform completion:', error);
      setPlatformCompletion(null);
    }
  }, []);

  // Fetch list of associates (for view settings feature; endpoint requires auth)
  const fetchAssociates = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    const r = await pulseRequest(`${API_BASE}/api/obd/associates`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (r.sessionExpired) { setAuthExpired(true); return; }
    if (r.ok && Array.isArray(r.data)) setAssociatesList(r.data);
    else if (!r.ok) console.error('Error fetching associates:', r.message);
  }, []);

  // Fetch all view settings (to show which associates have custom views)
  const fetchAllViewSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/obd/view-settings`);
      if (response.ok) {
        const data = await response.json();
        setAllViewSettings(data);
      }
    } catch (error) {
      console.error('Error fetching all view settings:', error);
    }
  }, []);

  // Reset all associate view settings
  const resetAllViewSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/obd/view-settings`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setAllViewSettings([]);
        setResetViewsDialogOpen(false);
        setSnackbar({
          open: true,
          message: 'All associate views have been reset to default',
          severity: 'success'
        });
      } else {
        throw new Error('Failed to reset');
      }
    } catch (error) {
      console.error('Error resetting view settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reset view settings',
        severity: 'error'
      });
    }
  };

  // Fetch saved view settings for current user (associate) or specified user (manager viewing as)
  const fetchViewSettings = useCallback(async (username) => {
    try {
      const response = await fetch(`${API_BASE}/api/obd/view-settings/${username}`);
      if (response.ok) {
        const data = await response.json();
        if (data.filters) {
          // Apply the saved filters
          const filters = typeof data.filters === 'string' ? JSON.parse(data.filters) : data.filters;
          // All filters are now arrays
          if (filters.platformFilter !== undefined) setPlatformFilter(Array.isArray(filters.platformFilter) ? filters.platformFilter : []);
          if (filters.statusFilter !== undefined) setStatusFilter(Array.isArray(filters.statusFilter) ? filters.statusFilter : []);
          if (filters.categoryFilter !== undefined) setCategoryFilter(Array.isArray(filters.categoryFilter) ? filters.categoryFilter : []);
          if (filters.shipToFilter !== undefined) setShipToFilter(Array.isArray(filters.shipToFilter) ? filters.shipToFilter : []);
          // Load exclude modes
          if (filters.platformExclude !== undefined) setPlatformExclude(!!filters.platformExclude);
          if (filters.statusExclude !== undefined) setStatusExclude(!!filters.statusExclude);
          if (filters.categoryExclude !== undefined) setCategoryExclude(!!filters.categoryExclude);
          if (filters.shipToExclude !== undefined) setShipToExclude(!!filters.shipToExclude);
          console.log(`📋 Loaded view settings for ${username}:`, filters);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error fetching view settings:', error);
      return false;
    }
  }, []);

  // Save current filters as view settings for an associate
  const saveViewSettings = async (username) => {
    try {
      const filters = {
        platformFilter,
        statusFilter,
        categoryFilter,
        shipToFilter,
        platformExclude,
        statusExclude,
        categoryExclude,
        shipToExclude
      };
      const response = await fetch(`${API_BASE}/api/obd/view-settings/${username}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          filters,
          setBy: currentUser?.username || 'Unknown'
        })
      });
      if (response.ok) {
        // Refresh the list of all view settings to update the indicator
        fetchAllViewSettings();
        setSnackbar({
          open: true,
          message: `View settings saved for ${username}`,
          severity: 'success'
        });
        return true;
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving view settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save view settings',
        severity: 'error'
      });
      return false;
    }
  };

  // Fetch historical data for a date range. Sends ISO datetimes to /api/obd/history,
  // mirroring Reports.js. Validation guard matches Reports' isValid pattern.
  const fetchHistoricalData = useCallback(async (start, end) => {
    if (!start || !isValid(start) || !end || !isValid(end)) {
      setError('Please enter a valid start and end date/time.');
      return;
    }
    setLoading(true);
    try {
      const startIso = start.toISOString();
      const endIso = end.toISOString();
      const histToken = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE}/api/obd/history?startDate=${encodeURIComponent(startIso)}&endDate=${encodeURIComponent(endIso)}`,
        { headers: histToken ? { Authorization: `Bearer ${histToken}` } : {} }
      );
      if (response.ok) {
        const data = await response.json();
        console.log('📅 Historical data:', data);
        setObds(data.obds);
        setHistoricalStats(data.stats);
        setError(null);
      } else {
        throw new Error('Failed to fetch historical data');
      }
    } catch (err) {
      console.error('Error fetching historical data:', err);
      setError('Failed to load historical data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch associate productivity stats
  const fetchAssociateStats = useCallback(async (name, period = 'day') => {
    setLoadingAssociateStats(true);
    try {
      const statsToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/obd/associate/${encodeURIComponent(name)}/stats?period=${period}`, {
        headers: statsToken ? { Authorization: `Bearer ${statsToken}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        console.log('👷 Associate stats:', data);
        setAssociateStats(data);
      } else {
        throw new Error('Failed to fetch associate stats');
      }
    } catch (err) {
      console.error('Error fetching associate stats:', err);
      setSnackbar({ open: true, message: 'Failed to load associate stats', severity: 'error' });
    } finally {
      setLoadingAssociateStats(false);
    }
  }, []);

  // Handle historical view toggle.
  // Toggle-on fetch is owned by the useEffect below (fires when isHistoricalView flips
  // to true) — avoids issuing two identical /api/obd/history requests back-to-back.
  // Toggle-off path stays imperative since live-mode fetches aren't governed by an effect.
  const handleHistoricalToggle = (e) => {
    const checked = e.target.checked;
    setIsHistoricalView(checked);
    if (!checked) {
      fetchAllOBDs();
      fetchSummary();
      setHistoricalStats(null);
      // Clear cross-history search when returning to live view
      setHistorySearchResults(null);
      setObdSearch('');
    }
  };

  // Re-fetch historical data when the range changes (only while historical view is on)
  useEffect(() => {
    if (isHistoricalView) {
      fetchHistoricalData(historicalStart, historicalEnd);
    }
  }, [historicalStart, historicalEnd, isHistoricalView, fetchHistoricalData]);

  // Export daily report
  const handleExportDaily = () => {
    if (isHistoricalView) {
      if (!historicalStart || !isValid(historicalStart) || !historicalEnd || !isValid(historicalEnd)) {
        return;
      }
      const startIso = historicalStart.toISOString();
      const endIso = historicalEnd.toISOString();
      const url = `${API_BASE}/api/obd/export/daily?startDate=${encodeURIComponent(startIso)}&endDate=${encodeURIComponent(endIso)}&format=csv&historical=true`;
      downloadExport(url, `obd-report-${startIso.slice(0, 10)}_to_${endIso.slice(0, 10)}.csv`);
    } else {
      const dateStr = getBusinessDay();
      const url = `${API_BASE}/api/obd/export/daily?date=${dateStr}&format=csv`;
      downloadExport(url, `obd-report-${dateStr}.csv`);
    }
  };

  // The export endpoint requires auth, and window.open can't send a header —
  // fetch with the JWT and hand the browser a blob instead.
  const downloadExport = async (url, filename) => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (response.status === 401) { localStorage.removeItem('authToken'); setAuthExpired(true); return; }
      if (!response.ok) {
        setSnackbar({ open: true, message: 'Export failed. Please try again.', severity: 'error' });
        return;
      }
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      setSnackbar({ open: true, message: "Can't reach PULSE. Check the connection and try again.", severity: 'error' });
    }
  };

  // Cross-history search — finds OBDs by number across ALL dates
  const handleHistorySearch = async () => {
    const term = obdSearch.trim();
    if (!term) return;
    setHistorySearchLoading(true);
    try {
      const r = await pulseRequest(
        `${API_BASE}/api/obd/search-history?number=${encodeURIComponent(term)}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } }
      );
      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (!r.ok) throw new Error(r.message);
      setHistorySearchResults(r.data);
    } catch (err) {
      console.error('History search error:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setHistorySearchLoading(false);
    }
  };

  // Open associate report modal
  const handleOpenAssociateReport = (worker) => {
    setSelectedAssociateForReport(worker);
    setAssociateModalOpen(true);
    fetchAssociateStats(worker.name, associateReportPeriod);
  };

  // Handle period change for associate report
  const handleAssociateReportPeriodChange = (e, newPeriod) => {
    if (newPeriod && selectedAssociateForReport) {
      setAssociateReportPeriod(newPeriod);
      fetchAssociateStats(selectedAssociateForReport.name, newPeriod);
    }
  };

  // Initial load
  useEffect(() => {
    fetchWorkers();
    fetchAllOBDs();
    fetchSummary();
    fetchCurrentLabor();
    fetchPlatformCompletion();

    // Load associates list and all view settings for managers
    if (canAccessManagerFeatures) {
      fetchAssociates();
      fetchAllViewSettings();
    }

    // Load saved view settings for associates
    if (currentUser?.role === 'associate' && currentUser?.username) {
      fetchViewSettings(currentUser.username);
    }
  }, [fetchWorkers, fetchAllOBDs, fetchSummary, fetchCurrentLabor, fetchPlatformCompletion, fetchAssociates, fetchAllViewSettings, fetchViewSettings, canAccessManagerFeatures, currentUser]);

  // Live timer update every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Change 6 (SOFT gate): load the admin pilot toggle for the clock-in nag.
  // Fails open (stays off) if the setting can't be read. Associates only ever
  // get nagged; managers/admins are skipped entirely in maybeNagToClockIn.
  useEffect(() => {
    if (!isAssociate) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/clock/nag-config`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : { enabled: false }))
      .then(d => { if (!cancelled) setNagEnabled(d.enabled === true); })
      .catch(() => { if (!cancelled) setNagEnabled(false); });
    return () => { cancelled = true; };
  }, [isAssociate]);

  // Open OBD details modal
  const handleOpenDetails = async (obd) => {
    setLoadingDetails(true);
    setDetailModalOpen(true);
    try {
      const response = await fetch(`${API_BASE}/api/obd/${obd.id}`);
      if (response.ok) {
        const data = await response.json();
        setObdDetails(data);
      } else {
        setSnackbar({ open: true, message: 'Failed to load OBD details', severity: 'error' });
      }
    } catch (err) {
      console.error('Error fetching OBD details:', err);
      setSnackbar({ open: true, message: 'Failed to load OBD details', severity: 'error' });
    } finally {
      setLoadingDetails(false);
    }
  };

  // Close OBD details modal
  const handleCloseDetails = () => {
    setDetailModalOpen(false);
    setObdDetails(null);
  };

  // Add new worker
  const handleAddWorker = async () => {
    setNewWorkerError('');

    if (!newWorkerName.trim()) {
      setNewWorkerError('Name is required');
      return;
    }

    if (!newWorkerEmployeeId.trim()) {
      setNewWorkerError('Employee ID is required');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/obd/workers`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: newWorkerName.trim(),
          employee_id: newWorkerEmployeeId.trim()
        })
      });

      if (response.ok) {
        await fetchWorkers();
        setNewWorkerName('');
        setNewWorkerEmployeeId('');
        setNewWorkerError('');
        setAddWorkerDialogOpen(false);
        setSnackbar({ open: true, message: 'Associate added successfully', severity: 'success' });
      } else {
        const result = await response.json();
        setNewWorkerError(result.error || 'Failed to add associate');
      }
    } catch (err) {
      console.error('Error adding worker:', err);
      setNewWorkerError('Failed to add associate');
    }
  };

  // ==================== EMPLOYEE ID VERIFICATION ====================
  // For associate accounts, verify employee ID before performing actions

  // Check if current user is an associate (requires verification)
  const requiresVerification = isAssociate;

  // AUTO-ASSIGN TOGGLE PROPAGATION (Oct 1): poll the config every 30 s and
  // re-fetch immediately on a start-next 409, switching modes IN PLACE —
  // ON → kiosk name grid, OFF → classic list. An associate is never left
  // on a dead "Start my next OBD" button after an admin flips the toggle.
  const refreshAutoAssign = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const r = await pulseRequest(`${API_BASE}/api/assignments/config`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok || !r.data) return; // keep the last known mode on a hiccup
      const next = !!r.data.enabled;
      setAutoAssignEnabled(prev => {
        if (prev !== next) fetchAllOBDs(); // mode flipped: refresh the list/counts now
        return next;
      });
    } catch { /* keep the last known mode */ }
  }, [fetchAllOBDs]);
  useEffect(() => {
    if (!isAssociate) return;
    const t = setInterval(refreshAutoAssign, 30000);
    return () => clearInterval(t);
  }, [isAssociate, refreshAutoAssign]);

  // Request verification for an action
  useEffect(() => {
    if (!isAssociate) return;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const r = await pulseRequest(`${API_BASE}/api/labor-standards/feedback-config`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setStandardsFeedbackEnabled(!!(r.ok && r.data && r.data.enabled));
        if (r.ok && r.data && Number.isFinite(Number(r.data.threshold))) setFeedbackThreshold(Number(r.data.threshold));
      } catch { /* stays off */ }
      try {
        await refreshAutoAssign();
      } catch { /* stays off — page behaves exactly as before */ }
      try {
        const token = localStorage.getItem('authToken');
        const r3 = await pulseRequest(`${API_BASE}/api/totes/config`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (r3.ok && r3.data && r3.data.enabled) setToteConfig(r3.data);
      } catch { /* stays off — no tote UI */ }
    })();
  }, [isAssociate, refreshAutoAssign]);

  const kioskMode = autoAssignEnabled && isAssociate;

  // "Can't pick" is a PEOPLE restriction (Oct 1): in the manual list
  // (toggle OFF) the selected associate's excluded categories gray out —
  // the claim endpoints enforce it server-side either way. The workers
  // payload resolves excluded groups to category names; excluded_general
  // covers unmapped categories via the GENERAL catch-all.
  const cantPickObd = (obd) => {
    if (!isAssociate || !selectedWorker) return false;
    const cat = obd.category_subset || obd.category;
    if ((selectedWorker.excluded_categories || []).includes(cat)) return true;
    return false; // unmapped/GENERAL case is enforced server-side
  };

  // clocked-in associates for the name grid (30s refresh)
  useEffect(() => {
    if (!kioskMode) return;
    let stop = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const r = await pulseRequest(`${API_BASE}/api/clock/active`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!stop && r.ok && Array.isArray(r.data)) setKioskRoster(r.data);
      } catch { /* grid just stays as-is */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { stop = true; clearInterval(t); };
  }, [kioskMode]);

  // 1s elapsed tick while someone is signed in on the kiosk
  useEffect(() => {
    if (!kioskMode || !kioskWorker) return;
    const t = setInterval(() => setKioskNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [kioskMode, kioskWorker]);

  // 20s of no interaction → back to the name grid (a number argument
  // overrides the delay — event objects from onClick/onPointerDown don't)
  const kioskTouch = useCallback((ms) => {
    const delay = typeof ms === 'number' ? ms : 20000;
    if (kioskIdleRef.current) clearTimeout(kioskIdleRef.current);
    kioskIdleRef.current = setTimeout(() => {
      setKioskWorker(null); setKioskGroup(null); setNextObdDialog(null); setKioskSearch('');
    }, delay);
  }, []);
  useEffect(() => {
    if (kioskWorker) kioskTouch();
    else if (kioskIdleRef.current) clearTimeout(kioskIdleRef.current);
  }, [kioskWorker, kioskTouch]);

  // NEXT-OBD POPUP LIFETIME (Oct 1): 90s auto-dismiss; the associate can
  // close it sooner with the X or a tap outside. While it's open the kiosk
  // idle reset is pushed past the popup window so the 20s idle-to-grid can't
  // eat it mid-read; after it closes the kiosk ACTIVE screen keeps the OBD
  // number, store and category on the screen (normal idle rules resume).
  useEffect(() => {
    if (!nextObdDialog) return undefined;
    if (kioskMode) kioskTouch(95000);
    const t = setTimeout(() => setNextObdDialog(null), 90000);
    return () => clearTimeout(t);
  }, [nextObdDialog, kioskMode, kioskTouch]);

  const kioskEnterFor = (rosterRow) => {
    // reuse the existing verify flow: the tapped name is the speed, the
    // employee ID stays the security
    const w = workers.find(x => String(x.employee_id || '').trim() === String(rosterRow.employee_id).trim())
      || { id: -1, name: rosterRow.worker_name, employee_id: rosterRow.employee_id };
    setSelectedWorker(w);
    requestVerification('kioskEnter');
  };

  // Advisory standards lookup for the verify dialog. Fire-and-forget: an error
  // or slow response just leaves the dialog without numbers — it must never
  // block, delay, or fail a start or complete, and touches no business logic.
  const loadVerifyFeedback = async (actionType, obdId, obdIds) => {
    try {
      const token = localStorage.getItem('authToken');
      const H = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      if (actionType === 'claim' || actionType === 'multiClaim') {
        const ids = actionType === 'claim' ? [obdId] : (obdIds || []);
        if (!ids.length) return;
        const r = await pulseRequest(`${API_BASE}/api/labor-standards/expected`, {
          method: 'POST', headers: H, body: JSON.stringify({ obd_ids: ids }),
        });
        // category with no standard → show nothing extra
        if (!r.ok || !r.data || !r.data.all_scoreable || !r.data.total_expected_seconds) return;
        const fb0 = { mode: 'start', expectedSeconds: r.data.total_expected_seconds, batch: ids.length > 1 };
        setVerifyFeedback(fb0);
        return fb0;
      } else if (actionType === 'complete') {
        const obd = obds.find(o => o.id === obdId);
        if (!obd || !obd.start_time) return;
        // Batch-level: completing one member completes its in-flight batch
        // siblings, so score the batch as one unit (never per synthetic row).
        const group = obd.batch_group_id
          ? obds.filter(o => o.batch_group_id === obd.batch_group_id && ['In Progress', 'Paused'].includes(o.status))
          : [obd];
        const r = await pulseRequest(`${API_BASE}/api/labor-standards/expected`, {
          method: 'POST', headers: H, body: JSON.stringify({ obd_ids: group.map(o => o.id) }),
        });
        if (!r.ok || !r.data || !r.data.all_scoreable) return;
        const earliest = Math.min(...group.map(o => new Date(o.start_time).getTime()));
        // SCORED TIME = span MINUS paused time (mirrors backend scoreUnits /
        // day-score, Oct 1): lead pauses and Activity-Monitor auto-pauses
        // never count against the verdict. An open pause (pause_start set)
        // counts up to now.
        const pausedSeconds = group.reduce((a, o) => a + (o.total_paused_seconds || 0)
          + (o.pause_start ? Math.max(0, (Date.now() - new Date(o.pause_start).getTime()) / 1000) : 0), 0);
        const actualSeconds = Math.max(0, (Date.now() - earliest) / 1000 - pausedSeconds);
        let dayPct = null;
        try {
          const worker = selectedWorker?.name || obd.assigned_worker || '';
          const d = await pulseRequest(`${API_BASE}/api/labor-standards/day-score?worker=${encodeURIComponent(worker)}`, { headers: H });
          if (d.ok && d.data && d.data.has_score) dayPct = d.data.pct_to_standard;
        } catch { /* day total is optional */ }
        const fb = { mode: 'complete', expectedSeconds: r.data.total_expected_seconds, actualSeconds, dayPct, batch: group.length > 1 };
        setVerifyFeedback(fb);
        return fb;
      }
    } catch { /* advisory only — never surface an error here */ }
    return null;
  };

  // Completion verdict for the auto-mode popup (Commit: two tiers + plain
  // minutes line). Computed from the verify-dialog snapshot; batch units stay
  // batch-scored; no standard / under 30s → neutral 'Not scored'.
  const buildCompletionVerdict = (fbOverride = null) => {
    if (!standardsFeedbackEnabled) return null; // feature dark → no verdict section
    const f = fbOverride || verifyFeedback;
    if (!f || f.mode !== 'complete' || !f.expectedSeconds) return { tier: 'not_scored', batch: !!f?.batch };
    const actual = Math.max(0, f.actualSeconds ?? 0);
    if (Math.round(actual / 60) === 0) return { tier: 'not_scored', batch: !!f.batch };
    const pct = Math.round(100 * f.expectedSeconds / actual);
    const diffMin = Math.round((actual - f.expectedSeconds) / 60);
    const tier = pct >= feedbackThreshold ? 'good' : 'needs';
    let line;
    if (Math.abs(diffMin) <= 1) line = 'Right on time';
    else if (diffMin > 0) line = `This OBD took ${diffMin} minutes longer than expected.`;
    else line = `Done ${-diffMin} minutes faster than expected.`;
    return { tier, pct, line, batch: !!f.batch };
  };

  // Ask the assignment engine for the associate's next OBD and start it.
  // Every failure path is friendly — a pick is never blocked by the engine.
  const executeStartNext = async (empId, verdict = null) => {
    try {
      const token = localStorage.getItem('authToken');
      const r = await pulseRequest(`${API_BASE}/api/assignments/start-next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ employee_id: empId }),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (!r.ok) {
        // a 409 here usually means the toggle flipped OFF under us —
        // re-fetch the mode immediately so the page switches to the
        // classic list instead of leaving a dead kiosk button
        if (r.status === 409) refreshAutoAssign();
        setNextObdDialog({ empId, verdict, message: r.message || 'Assignment is unavailable — see your lead.' });
        return;
      }
      if (!r.data.assigned) {
        setNextObdDialog({ empId, verdict, message: r.data.message });
        await fetchAllOBDs();
        return;
      }
      const dlg = { empId, verdict, obd: r.data.obd };
      setNextObdDialog(dlg);
      await fetchAllOBDs();
      await fetchSummary();
      if (standardsFeedbackEnabled) {
        try {
          const fe = await pulseRequest(`${API_BASE}/api/labor-standards/expected`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ obd_ids: [r.data.obd.id] }),
          });
          if (fe.ok && fe.data && fe.data.all_scoreable) {
            setNextObdDialog({ ...dlg, expectedSeconds: fe.data.total_expected_seconds });
          }
        } catch { /* advisory only */ }
      }
    } catch {
      setNextObdDialog({ empId, verdict, message: 'Assignment is unavailable — see your lead.' });
    }
  };

  // "Taking a break" button REMOVED (Oct 1): breaks, lunch and activity
  // changes go through the Activity Monitor, whose hooks release a <150s
  // OBD or auto-pause an older one (paused time never counts against the
  // score). POST /assignments/release still exists for the Activity Monitor
  // path; the assignment_log history of past button uses is kept.

  const requestVerification = (actionType, obdId = null, obdIds = null) => {
    setPendingAction({ type: actionType, obdId, obdIds });
    setVerifyEmployeeId('');
    setVerifyError('');
    setVerifyFeedback(null);
    if (standardsFeedbackEnabled) loadVerifyFeedback(actionType, obdId, obdIds); // fire-and-forget
    setVerifyDialogOpen(true);
  };

  // Verify employee ID and execute pending action
  const handleVerifyAndExecute = async () => {
    if (!verifyEmployeeId.trim()) {
      setVerifyError('Please enter your Employee ID');
      return;
    }

    setVerifyLoading(true);
    setVerifyError('');

    try {
      // Verify employee ID against database (endpoint requires auth — the
      // associate account is logged in, so the token is always available)
      const verifyToken = localStorage.getItem('authToken');
      const r = await pulseRequest(`${API_BASE}/api/obd/verify-employee`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(verifyToken ? { Authorization: `Bearer ${verifyToken}` } : {}) },
        body: JSON.stringify({
          name: selectedWorker?.name,
          employeeId: verifyEmployeeId.trim()
        })
      });

      if (r.sessionExpired) { setAuthExpired(true); return; }
      const result = r.data || {};

      if (!result.valid) {
        setVerifyError((r.ok || r.status === 400 ? result.error : r.message) || 'Invalid Employee ID');
        setVerifyLoading(false);
        return;
      }

      // Employee ID verified - execute the pending action
      const action = pendingAction;
      const empId = verifyEmployeeId.trim();

      setVerifyDialogOpen(false);
      setVerifyLoading(false);
      setPendingAction(null);

      switch (action.type) {
        case 'kioskEnter': {
          // verified: open their kiosk session and load the category context
          setKioskWorker({ employee_id: empId, name: selectedWorker?.name || '' });
          try {
            const token = localStorage.getItem('authToken');
            const g = await pulseRequest(`${API_BASE}/api/assignments/my-group?employee_id=${encodeURIComponent(empId)}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            setKioskGroup(g.ok ? (g.data?.group || null) : null);
          } catch { setKioskGroup(null); }
          break;
        }
        case 'startNext':
          await executeStartNext(empId);
          break;
        case 'claim':
          await executeClaimOBD(action.obdId);
          break;
        case 'complete':
          await executeCompleteOBD(action.obdId, empId);
          break;
        case 'pause':
          await executePauseOBD(action.obdId);
          break;
        case 'resume':
          await executeResumeOBD(action.obdId);
          break;
        case 'multiClaim':
          await executeMultiOBDClaim();
          break;
        default:
          console.error('Unknown action type:', action.type);
      }

      // The pick already happened above — NEVER blocked by anything below.
      // Auto check-in to Picking (primary), nag (fallback). Fire-and-forget,
      // once per claim action (not per OBD), associates-only path (managers
      // don't verify so they never reach here).
      if (action.type === 'claim' || action.type === 'multiClaim') {
        ensurePickingOrNag(empId);
      }
    } catch (err) {
      console.error('Error verifying employee ID:', err);
      setVerifyError('Failed to verify Employee ID');
      setVerifyLoading(false);
    }
  };

  // Cancel verification
  const handleCancelVerify = () => {
    setVerifyDialogOpen(false);
    setPendingAction(null);
    setVerifyEmployeeId('');
    setVerifyError('');
    setVerifyFeedback(null);
  };

  // ==================== OBD ACTIONS ====================

  // Claim OBD (start working)
  const handleClaimOBD = async (obdId) => {
    if (!selectedWorker) {
      setSnackbar({ open: true, message: 'Please select an associate first', severity: 'warning' });
      return;
    }

    // Associates need to verify Employee ID first
    if (requiresVerification) {
      requestVerification('claim', obdId);
      return;
    }

    await executeClaimOBD(obdId);
  };

  // Change 6 (SOFT gate): check whether an associate is clocked in, purely to decide
  // whether to nag. Fails OPEN (returns allowed) on any clock-service error so the
  // nag never fires — and never interferes with — the picking flow on a clock outage.
  const checkClockedIn = async (employeeId) => {
    try {
      const resp = await fetch(
        `${API_BASE}/api/clock/picking-allowed?employee_id=${encodeURIComponent(employeeId)}`,
        { headers: getAuthHeaders() }
      );
      if (!resp.ok) return { allowed: true };
      return await resp.json();
    } catch (err) {
      console.error('Clock nag check failed, skipping nag:', err);
      return { allowed: true };
    }
  };

  // Auto check-in to Picking on OBD claim (primary layer). Fires fire-and-forget
  // AFTER a successful claim; the pick is never delayed, blocked, or errored by
  // this. FAIL-OPEN CHAIN: if ensure-picking errors, is unreachable, or reports
  // the toggle is off, fall back to the EXISTING nag behavior exactly as today
  // (which itself fails open). When the toggle is on, the nag naturally never
  // fires for its original cases — the state it checks gets fixed first.
  const ensurePickingOrNag = async (employeeId) => {
    if (!employeeId) return;
    try {
      const resp = await fetch(`${API_BASE}/api/clock/ensure-picking`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ employee_id: employeeId }),
      });
      if (!resp.ok) { maybeNagToClockIn(employeeId); return; }
      const data = await resp.json();
      if (data.action === 'disabled') { maybeNagToClockIn(employeeId); return; }
      // Corrective actions (their state had to be fixed) → warning DIALOG.
      // Legitimate switch → quiet toast. noop → silent.
      if (data.action === 'checked_in') {
        setAutoPickWarn({
          open: true,
          message: "You were not checked in to the Activity Monitor before starting this OBD. We've automatically checked you in under Picking. Please check in at the start of your shift — if this continues, your manager may be notified.",
        });
      } else if (data.action === 'ended_break') {
        setAutoPickWarn({
          open: true,
          message: "You were on break when you started this OBD. We've ended your break and checked you in under Picking. Please end your break at the Activity Monitor before picking — if this continues, your manager may be notified.",
        });
      } else if (data.action === 'switched') {
        setSnackbar({ open: true, message: 'Switched to Picking', severity: 'success' });
      }
    } catch (err) {
      console.error('ensure-picking failed, falling back to nag:', err);
      maybeNagToClockIn(employeeId);
    }
  };

  // Show the check-in nag if enabled and this associate isn't checked in.
  // FALLBACK layer behind ensurePickingOrNag — unchanged behavior, still fails open.
  // Fires per OBD start only (no recurring timer — see state comment above).
  const maybeNagToClockIn = async (employeeId) => {
    if (!nagEnabled || !employeeId) return;
    const clock = await checkClockedIn(employeeId);
    if (clock.allowed) return;
    setClockNag({
      open: true,
      message: clock.status === 'on_break'
        ? "You're on break/lunch. Please end your break and check in."
        : "You haven't checked in. Please check in.",
    });
  };

  // Execute claim (after verification if needed). Picking is NEVER gated on clock state.
  const executeClaimOBD = async (obdId) => {
    if (!selectedWorker) {
      setSnackbar({ open: true, message: 'Please select an associate first', severity: 'warning' });
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/obd/${obdId}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workerName: selectedWorker.name })
      });

      if (response.ok) {
        await fetchAllOBDs();
        await fetchSummary();
        setSelectedOBDs([]); // Clear selection after claiming
        setSnackbar({ open: true, message: 'OBD started!', severity: 'success' });
      } else if (response.status === 409) {
        // Concurrent claim - someone else got it first
        const result = await response.json();
        const claimedAt = result.claimed_at ? new Date(result.claimed_at).toLocaleTimeString() : 'recently';
        setSnackbar({
          open: true,
          message: `This OBD was claimed by ${result.claimed_by} at ${claimedAt}. Refreshing list...`,
          severity: 'warning'
        });
        await fetchAllOBDs();
        await fetchSummary();
        setSelectedOBDs([]);
      } else {
        const result = await response.json();
        setSnackbar({ open: true, message: result.error || 'Failed to claim OBD', severity: 'error' });
      }
    } catch (err) {
      console.error('Error claiming OBD:', err);
      setSnackbar({ open: true, message: 'Failed to claim OBD', severity: 'error' });
    }
  };

  // Complete OBD (and all siblings if part of a batch)
  const handleCompleteOBD = async (obdId) => {
    // Associates need to verify Employee ID first
    if (requiresVerification) {
      requestVerification('complete', obdId);
      return;
    }

    await executeCompleteOBD(obdId);
  };

  // Execute complete (after verification if needed)
  const executeCompleteOBD = async (obdId, verifiedEmpId = null, fbOverride = null) => {
    try {
      const response = await fetch(`${API_BASE}/api/obd/${obdId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        await fetchAllOBDs();
        await fetchSummary();

        // Show message about batch completion if applicable
        const siblingCount = result.siblingAssignmentsCompleted || 0;
        const message = siblingCount > 0
          ? `Completed ${siblingCount + 1} OBDs! Total: ${result.durationMinutes} minutes`
          : `OBD completed! Duration: ${result.durationMinutes} minutes`;

        setSnackbar({
          open: true,
          message,
          severity: 'success'
        });
        // Auto-assignment: one popup — the completion verdict plus their
        // next OBD (store number as the headline).
        if (autoAssignEnabled && isAssociate && verifiedEmpId) {
          await executeStartNext(verifiedEmpId, buildCompletionVerdict(fbOverride));
        }
      }
    } catch (err) {
      console.error('Error completing OBD:', err);
      setSnackbar({ open: true, message: 'Failed to complete OBD', severity: 'error' });
    }
  };

  // Pause OBD (for lunch break, etc.)
  const handlePauseOBD = async (obdId) => {
    // Associates need to verify Employee ID first
    if (requiresVerification) {
      requestVerification('pause', obdId);
      return;
    }

    await executePauseOBD(obdId);
  };

  // Execute pause (after verification if needed)
  const executePauseOBD = async (obdId) => {
    console.log('⏸️ Pausing OBD:', obdId);
    try {
      // Pause is lead-only on the backend — send the JWT so manager/admin
      // sessions authorize (associates get the friendly 403 surfaced below)
      const token = localStorage.getItem('authToken');
      const r = await pulseRequest(`${API_BASE}/api/obd/${obdId}/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      console.log('⏸️ Pause response:', r.status);

      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (r.ok) {
        console.log('✅ OBD paused successfully');
        await fetchAllOBDs();
        await fetchSummary();
        setSnackbar({
          open: true,
          message: 'OBD paused. You can resume when ready.',
          severity: 'info'
        });
      } else {
        // e.g. the lead-only 403: "Ask a lead to pause this OBD"
        setSnackbar({ open: true, message: r.message, severity: 'error' });
      }
    } catch (err) {
      console.error('❌ Error pausing OBD:', err);
      setSnackbar({ open: true, message: 'Failed to pause OBD', severity: 'error' });
    }
  };

  // Resume paused OBD
  const handleResumeOBD = async (obdId) => {
    // Associates need to verify Employee ID first
    if (requiresVerification) {
      requestVerification('resume', obdId);
      return;
    }

    await executeResumeOBD(obdId);
  };

  // Execute resume (after verification if needed)
  const executeResumeOBD = async (obdId) => {
    console.log('▶️ Resuming OBD:', obdId);
    try {
      // Send the JWT when present so lead-initiated resumes stamp resumed_by;
      // associate self-resumes work with or without it
      const resumeToken = localStorage.getItem('authToken');
      // Single-picker rule: the backend only lets the OWNER self-resume, so
      // send the verified associate's name; a manager/admin token resumes
      // (and may reassign) anyone's.
      const r = await pulseRequest(`${API_BASE}/api/obd/${obdId}/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(resumeToken ? { Authorization: `Bearer ${resumeToken}` } : {}) },
        body: JSON.stringify({ workerName: selectedWorker?.name || null })
      });
      console.log('▶️ Resume response:', r.status);

      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (r.ok) {
        console.log('✅ OBD resumed successfully');
        await fetchAllOBDs();
        await fetchSummary();
        setSnackbar({
          open: true,
          message: 'OBD resumed!',
          severity: 'success'
        });
      } else {
        setSnackbar({ open: true, message: r.message, severity: 'error' });
      }
    } catch (err) {
      console.error('❌ Error resuming OBD:', err);
      setSnackbar({ open: true, message: 'Failed to resume OBD', severity: 'error' });
    }
  };

  // Unassign an OBD (manager only)
  const handleUnassignOBD = async (obdId) => {
    console.log('↩️ Unassigning OBD:', obdId);
    try {
      const response = await fetch(`${API_BASE}/api/obd/${obdId}/unclaim`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ OBD unassigned successfully');
        await fetchAllOBDs();
        await fetchSummary();
        setSnackbar({ open: true, message: 'Assignment removed', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to unassign', severity: 'error' });
      }
    } catch (err) {
      console.error('❌ Error unassigning OBD:', err);
      setSnackbar({ open: true, message: 'Failed to unassign', severity: 'error' });
    }
  };

  // Reset a completed OBD back to Available (manager/admin only)
  const handleResetOBD = async () => {
    if (!resetTargetObd) return;

    console.log('🔄 Resetting OBD:', resetTargetObd.obd_number);
    try {
      const response = await fetch(`${API_BASE}/api/obd/${resetTargetObd.id}/reset`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ OBD reset successfully');
        await fetchAllOBDs();
        await fetchSummary();
        setSnackbar({
          open: true,
          message: `OBD ${resetTargetObd.obd_number} has been reset to Available`,
          severity: 'success'
        });
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to reset OBD', severity: 'error' });
      }
    } catch (err) {
      console.error('❌ Error resetting OBD:', err);
      setSnackbar({ open: true, message: 'Failed to reset OBD', severity: 'error' });
    } finally {
      setResetDialogOpen(false);
      setResetTargetObd(null);
    }
  };

  // Auth headers helper for manager-only endpoints
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  // Bulk delete Available OBDs (manager/admin only)
  const handleBulkDelete = async () => {
    try {
      const r = await pulseRequest(`${API_BASE}/api/obd/bulk-delete`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ obd_ids: selectedOBDs })
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (r.ok) {
        setSnackbar({ open: true, message: `Deleted ${r.data.deletedCount} OBD${r.data.deletedCount !== 1 ? 's' : ''}`, severity: 'success' });
        handleClearSelection();
        await fetchAllOBDs();
        await fetchSummary();
      } else {
        setSnackbar({ open: true, message: r.message, severity: 'error' });
      }
    } catch (err) {
      console.error('❌ Error bulk deleting OBDs:', err);
      setSnackbar({ open: true, message: 'Failed to delete OBDs', severity: 'error' });
    } finally {
      setBulkDeleteDialogOpen(false);
    }
  };

  // Reassign an OBD to a different associate (manager only)
  const handleReassignOBD = async (obdId, newWorkerName) => {
    console.log('🔄 Reassigning OBD:', obdId, 'to', newWorkerName);
    try {
      const response = await fetch(`${API_BASE}/api/obd/${obdId}/reassign`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ workerName: newWorkerName })
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ OBD reassigned successfully');
        await fetchAllOBDs();
        await fetchSummary();
        setReassignDialogOpen(false);
        setReassignTargetObd(null);
        setSnackbar({ open: true, message: `Reassigned to ${newWorkerName}`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to reassign', severity: 'error' });
      }
    } catch (err) {
      console.error('❌ Error reassigning OBD:', err);
      setSnackbar({ open: true, message: 'Failed to reassign', severity: 'error' });
    }
  };

  // Pre-assign an Available OBD to a specific associate (manager/admin only)
  const handlePreassignOBD = async (obdId, workerName) => {
    try {
      const response = await fetch(`${API_BASE}/api/obd/${obdId}/preassign`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ workerName })
      });

      const result = await response.json();

      if (response.ok) {
        await fetchAllOBDs();
        await fetchSummary();
        setPreassignDialogOpen(false);
        setPreassignTargetObd(null);
        setSnackbar({ open: true, message: `Assigned to ${workerName}`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to assign', severity: 'error' });
      }
    } catch (err) {
      console.error('Error pre-assigning OBD:', err);
      setSnackbar({ open: true, message: 'Failed to assign', severity: 'error' });
    }
  };

  // Bulk pre-assign selected Available OBDs to one associate (manager/admin only)
  const handleBulkAssign = async (workerName) => {
    try {
      const r = await pulseRequest(`${API_BASE}/api/obd/bulk-preassign`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ obd_ids: selectedOBDs, worker_name: workerName })
      });

      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (r.ok) {
        setBulkAssignDialogOpen(false);
        handleClearSelection();
        await fetchAllOBDs();
        await fetchSummary();
        setSnackbar({
          open: true,
          message: `Assigned ${r.data.count} OBD${r.data.count !== 1 ? 's' : ''} to ${r.data.assignedTo}`,
          severity: 'success'
        });
      } else {
        setSnackbar({ open: true, message: r.message, severity: 'error' });
      }
    } catch (err) {
      console.error('Error bulk pre-assigning OBDs:', err);
      setSnackbar({ open: true, message: 'Failed to assign OBDs', severity: 'error' });
    }
  };

  // Multi-select handlers
  const handleToggleSelect = (obdId) => {
    setSelectedAssignments(prev =>
      prev.includes(obdId)
        ? prev.filter(id => id !== obdId)
        : [...prev, obdId]
    );
  };

  const handleSelectAll = (obdIds) => {
    setSelectedAssignments(obdIds);
  };

  const handleClearSelection = () => {
    setSelectedOBDs([]);
  };

  // Batch claim multiple assignments (same OBD number required)
  const handleBatchClaim = async () => {
    if (!selectedWorker) {
      setSnackbar({ open: true, message: 'Please select an associate first', severity: 'warning' });
      return;
    }

    if (selectedAssignments.length === 0) {
      setSnackbar({ open: true, message: 'No assignments selected', severity: 'warning' });
      return;
    }

    // Verify all selected are from the same OBD
    const selectedObds = obds.filter(o => selectedAssignments.includes(o.id));
    const obdNumbers = [...new Set(selectedObds.map(o => o.obd_number))];

    if (obdNumbers.length > 1) {
      setSnackbar({
        open: true,
        message: 'All selected assignments must be from the same OBD number',
        severity: 'error'
      });
      return;
    }

    // Verify all are Available
    const nonAvailable = selectedObds.filter(o => o.status !== 'Available');
    if (nonAvailable.length > 0) {
      setSnackbar({
        open: true,
        message: 'Some selected assignments are not available',
        severity: 'error'
      });
      return;
    }

    console.log('📦 Batch claiming:', selectedAssignments.length, 'assignments');
    try {
      const response = await fetch(`${API_BASE}/api/obd/batch-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: selectedAssignments,
          workerName: selectedWorker.name
        })
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ Batch claim successful:', result);
        await fetchAllOBDs();
        await fetchSummary();
        setSelectedAssignments([]);
        setSnackbar({
          open: true,
          message: `Started ${result.assignmentsClaimed} assignments for OBD ${result.obdNumber}`,
          severity: 'success'
        });
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to batch claim', severity: 'error' });
      }
    } catch (err) {
      console.error('❌ Error batch claiming:', err);
      setSnackbar({ open: true, message: 'Failed to batch claim', severity: 'error' });
    }
  };

  // Multi-OBD claim: multiple OBDs from same store (no line count restriction)
  // Time is distributed proportionally by line count on completion
  const handleMultiOBDClaim = async () => {
    if (autoAssignEnabled && isAssociate) {
      setSnackbar({ open: true, message: 'OBDs are assigned automatically — tap "Start my next OBD".', severity: 'info' });
      return;
    }
    if (!selectedWorker) {
      setSnackbar({ open: true, message: 'Please select an associate first', severity: 'warning' });
      return;
    }

    if (selectedOBDs.length < 2) {
      setSnackbar({ open: true, message: 'Select at least 2 OBDs to start together', severity: 'warning' });
      return;
    }

    const selectedObdList = obds.filter(o => selectedOBDs.includes(o.id));

    // Verify all are Available
    const nonAvailable = selectedObdList.filter(o => o.status !== 'Available');
    if (nonAvailable.length > 0) {
      setSnackbar({
        open: true,
        message: 'All selected OBDs must be Available',
        severity: 'error'
      });
      return;
    }

    // Verify all have same ship_to_code
    const shipToCodes = [...new Set(selectedObdList.map(o => o.ship_to_code))];
    if (shipToCodes.length > 1) {
      setSnackbar({
        open: true,
        message: 'All selected OBDs must be from the same store',
        severity: 'error'
      });
      return;
    }

    // Verify all have same category
    const categories = [...new Set(selectedObdList.map(o => o.category))];
    if (categories.length > 1) {
      setSnackbar({
        open: true,
        message: 'All selected OBDs must be from the same category',
        severity: 'error'
      });
      return;
    }

    // Associates need to verify Employee ID first
    if (requiresVerification) {
      requestVerification('multiClaim', null, selectedOBDs);
      return;
    }

    await executeMultiOBDClaim();
  };

  // Execute multi-OBD claim (after verification if needed). Never gated on clock state.
  const executeMultiOBDClaim = async () => {
    const selectedObdList = obds.filter(o => selectedOBDs.includes(o.id));
    const shipToCodes = [...new Set(selectedObdList.map(o => o.ship_to_code))];
    const totalLines = selectedObdList.reduce((sum, o) => sum + (o.total_lines || 0), 0);
    console.log('📦 Starting', selectedOBDs.length, 'OBDs from store', shipToCodes[0], '- total lines:', totalLines);

    try {
      const response = await fetch(`${API_BASE}/api/obd/multi-obd-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: selectedOBDs,
          workerName: selectedWorker.name
        })
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ Multi-OBD claim successful:', result);
        await fetchAllOBDs();
        await fetchSummary();
        setSelectedOBDs([]);
        setSnackbar({
          open: true,
          message: `Started ${result.assignmentsClaimed} OBDs (${result.totalLines} lines) from store ${result.shipToCode}`,
          severity: 'success'
        });
      } else if (response.status === 409) {
        // Concurrent claim - some OBDs were already claimed
        console.log('⚠️ Concurrent claim conflict:', result);
        const claimedList = result.alreadyClaimed || [];
        const claimedOBDs = claimedList.map(c => `${c.obd_number} (${c.claimed_by})`).join(', ');
        setSnackbar({
          open: true,
          message: claimedList.length === 1
            ? `OBD ${claimedList[0].obd_number} was already claimed by ${claimedList[0].claimed_by}. Refreshing list...`
            : `${claimedList.length} OBDs were already claimed: ${claimedOBDs}. Refreshing list...`,
          severity: 'warning'
        });
        await fetchAllOBDs();
        await fetchSummary();
        setSelectedOBDs([]);
      } else {
        setSnackbar({ open: true, message: result.message || result.error || 'Failed to start OBDs', severity: 'error' });
      }
    } catch (err) {
      console.error('❌ Error starting multiple OBDs:', err);
      setSnackbar({ open: true, message: 'Failed to start OBDs', severity: 'error' });
    }
  };

  // Open Edit Labor dialog
  const handleOpenLaborDialog = () => {
    if (currentLabor) {
      setEditFtAssociates(currentLabor.ft_associates || 0);
      setEditPtAssociates(currentLabor.pt_associates || 0);
      // Parse shift_start_time from database format (could be "18:00:00" or "18:00")
      const shiftTime = currentLabor.shift_start_time || '15:00';
      setEditShiftStartTime(shiftTime.substring(0, 5)); // Take just HH:MM
      setLaborDialogOpen(true);
    }
  };

  // Update labor plan
  const handleUpdateLabor = async () => {
    if (!currentLabor) return;

    try {
      const response = await fetch(`${API_BASE}/api/obd/imports/${currentLabor.id}/labor`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ftAssociates: editFtAssociates,
          ptAssociates: editPtAssociates,
          shiftStartTime: editShiftStartTime
        })
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ Labor plan updated:', result);
        await fetchCurrentLabor();
        setLaborDialogOpen(false);
        const shiftTimeFormatted = new Date(`2000-01-01T${editShiftStartTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        setSnackbar({
          open: true,
          message: `Labor plan updated: ${editFtAssociates} FT + ${editPtAssociates} PT @ ${shiftTimeFormatted}`,
          severity: 'success'
        });
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to update labor plan', severity: 'error' });
      }
    } catch (err) {
      console.error('❌ Error updating labor plan:', err);
      setSnackbar({ open: true, message: 'Failed to update labor plan', severity: 'error' });
    }
  };

  // Import OBDs from SAP data
  const handleImport = async () => {
    if (!importData.trim()) {
      setSnackbar({ open: true, message: 'Please paste SAP data first', severity: 'warning' });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/obd/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          rawData: importData,
          importedBy: 'Manager',
          ftAssociates: ftAssociates,
          ptAssociates: ptAssociates,
          shiftStartTime: shiftStartTime
        })
      });

      const result = await response.json();

      if (response.ok) {
        setImportResult(result);
        setImportData('');

        // Store labor analysis and open dialog if we have labor data
        if (result.laborAnalysis) {
          setLaborAnalysis(result.laborAnalysis);
          setLaborAnalysisDialogOpen(true);
        }

        await fetchAllOBDs();
        await fetchSummary();
        await fetchCurrentLabor();

        // Reset labor inputs after successful import
        setFtAssociates(0);
        setPtAssociates(0);
        setShiftStartTime('15:00');

        // Check for duplicates warning
        const duplicateWarning = result.duplicatesSkipped > 0
          ? ` (${result.duplicatesSkipped} duplicates skipped)`
          : '';

        setSnackbar({
          open: true,
          message: `Imported ${result.summary.totalObds} OBDs successfully!${duplicateWarning}`,
          severity: result.duplicatesSkipped > 0 ? 'warning' : 'success'
        });
      } else {
        setSnackbar({ open: true, message: result.error || 'Import failed', severity: 'error' });
      }
    } catch (err) {
      console.error('Error importing OBDs:', err);
      setSnackbar({ open: true, message: 'Failed to import OBDs', severity: 'error' });
    } finally {
      setImporting(false);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setPlatformFilter([]);
    setStatusFilter([]);
    setCategoryFilter([]);
    setShipToFilter([]);
    setPlatformExclude(false);
    setStatusExclude(false);
    setCategoryExclude(false);
    setShipToExclude(false);
    setObdSearch('');
  };

  // Handle sort
  const handleSort = (column) => {
    console.log('🔀 Sort clicked:', column, 'current:', sortColumn, sortDirection);
    if (sortColumn === column) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      console.log('🔀 Toggling direction to:', newDirection);
      setSortDirection(newDirection);
    } else {
      console.log('🔀 Changing column to:', column);
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Reset to default sort (store priority order)
  const handleResetSort = () => {
    setSortColumn('default');
    setSortDirection('asc');
  };

  // Format time for display
  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate duration between start and end time, minus paused time
  const calculateDuration = (startTime, endTime, totalPausedSeconds = 0) => {
    if (!startTime || !endTime) return null;
    const start = new Date(startTime);
    const end = new Date(endTime);
    const grossMs = end - start;
    const pausedMs = (totalPausedSeconds || 0) * 1000;
    const netMs = Math.max(0, grossMs - pausedMs); // Ensure non-negative
    const diffSecs = Math.floor(netMs / 1000);
    const hours = Math.floor(diffSecs / 3600);
    const minutes = Math.floor((diffSecs % 3600) / 60);
    const seconds = diffSecs % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  // Calculate live duration (net working time, excluding paused time)
  const calculateLiveDuration = (startTime, totalPausedSeconds = 0, pauseStart = null) => {
    if (!startTime) return '-';
    const start = new Date(startTime);
    let diffSecs = Math.floor((currentTime - start) / 1000);

    // Subtract already-accumulated pause time
    diffSecs -= (totalPausedSeconds || 0);

    // If currently paused, also subtract the ongoing pause duration
    if (pauseStart) {
      const pauseStartTime = new Date(pauseStart);
      diffSecs -= Math.floor((currentTime - pauseStartTime) / 1000);
    }

    diffSecs = Math.max(0, diffSecs);
    const hours = Math.floor(diffSecs / 3600);
    const minutes = Math.floor((diffSecs % 3600) / 60);
    const seconds = diffSecs % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  // Calculate time until departure
  const getTimeUntilDeparture = (departureTime) => {
    if (!departureTime) return null;

    const now = new Date();
    const [hours, minutes] = departureTime.split(':').map(Number);
    const departure = new Date();
    departure.setHours(hours, minutes, 0, 0);

    if (departure < now) {
      departure.setDate(departure.getDate() + 1);
    }

    const diffMs = departure - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours < 0) return 'Departed';
    if (diffHours === 0) return `${diffMins}m`;
    return `${diffHours}h ${diffMins}m`;
  };

  // Get status chip
  // If autoCompleted is true, show "Auto-Completed" in gray/orange with tooltip
  const getStatusChip = (status, autoCompleted = false) => {
    switch (status) {
      case 'Available':
        return <Chip label="Available" color="success" size="small" icon={<PlayArrowIcon />} />;
      case 'In Progress':
        return <Chip label="In Progress" color="warning" size="small" icon={<AccessTimeIcon />} />;
      case 'Paused':
        return <Chip label="Paused" color="info" size="small" icon={<PauseIcon />} />;
      case 'Completed':
        if (autoCompleted) {
          return (
            <Tooltip title="Auto-completed on import - excluded from productivity reporting">
              <Chip
                label="Auto-Completed"
                size="small"
                icon={<CheckCircleIcon />}
                sx={{
                  backgroundColor: '#9e9e9e',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            </Tooltip>
          );
        }
        return <Chip label="Completed" color="default" size="small" icon={<CheckCircleIcon />} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  // Get category chip
  // Note: OBDs are now split by category, so has_mixed_categories is always false
  const getCategoryChip = (category) => {
    const color = CATEGORY_COLORS[category] || CATEGORY_COLORS['UNKNOWN'];
    return (
      <Chip
        label={category || 'UNKNOWN'}
        size="small"
        sx={{
          backgroundColor: color,
          color: 'white',
          fontWeight: 500
        }}
      />
    );
  };

  // Get platform chip
  const getPlatformChip = (platform) => {
    const color = PLATFORM_COLORS[platform] || PLATFORM_COLORS['UNKNOWN'];
    return (
      <Chip
        label={platform || '?'}
        size="small"
        sx={{
          backgroundColor: color,
          color: 'white',
          fontWeight: 600,
          minWidth: 50
        }}
      />
    );
  };

  // Get active OBDs for current worker (includes In Progress and Paused)
  const getActiveOBDsForWorker = () => {
    if (!selectedWorker) return [];
    return obds.filter(obd =>
      (obd.status === 'In Progress' || obd.status === 'Paused') &&
      obd.assigned_worker === selectedWorker.name
    );
  };

  // Filter and sort OBDs
  const filteredAndSortedOBDs = useMemo(() => {
    console.log('🔍 Filtering/Sorting - obds:', obds.length, 'filters:', { platformFilter, statusFilter, categoryFilter, shipToFilter, obdSearch }, 'sort:', sortColumn, sortDirection);
    let filtered = [...obds];

    // Apply OBD number search filter (case-insensitive partial match)
    if (obdSearch) {
      filtered = filtered.filter(o => o.obd_number.toLowerCase().includes(obdSearch.toLowerCase()));
      console.log('🔍 After OBD search filter:', filtered.length);
    }

    // Apply filters (all are multi-select arrays with include/exclude modes)
    if (platformFilter.length > 0) {
      if (platformExclude) {
        filtered = filtered.filter(o => !platformFilter.includes(o.platform));
      } else {
        filtered = filtered.filter(o => platformFilter.includes(o.platform));
      }
      console.log('🔍 After platform filter:', filtered.length, platformExclude ? '(exclude)' : '(include)');
    }
    if (statusFilter.length > 0) {
      if (statusExclude) {
        filtered = filtered.filter(o => !statusFilter.includes(o.status));
      } else {
        filtered = filtered.filter(o => statusFilter.includes(o.status));
      }
      console.log('🔍 After status filter:', filtered.length, statusExclude ? '(exclude)' : '(include)');
    }
    if (categoryFilter.length > 0) {
      if (categoryExclude) {
        filtered = filtered.filter(o => !categoryFilter.includes(o.category));
      } else {
        filtered = filtered.filter(o => categoryFilter.includes(o.category));
      }
      console.log('🔍 After category filter:', filtered.length, categoryExclude ? '(exclude)' : '(include)');
    }
    if (shipToFilter.length > 0) {
      if (shipToExclude) {
        filtered = filtered.filter(o => !shipToFilter.includes(o.ship_to_code));
      } else {
        filtered = filtered.filter(o => shipToFilter.includes(o.ship_to_code));
      }
      console.log('🔍 After shipTo filter:', filtered.length, shipToExclude ? '(exclude)' : '(include)');
    }

    // Hide completed if toggle is off
    if (!showCompleted) {
      filtered = filtered.filter(o => o.status !== 'Completed');
    }

    // When a name is selected: hide OBDs assigned to OTHER workers.
    // Unassigned OBDs and OBDs assigned to the selected worker remain visible.
    if (selectedWorker) {
      filtered = filtered.filter(o =>
        !o.assigned_worker || o.assigned_worker === selectedWorker.name
      );
    }

    // Sort
    // Status ordering: Available at top, then In Progress, Paused, Completed at bottom
    const statusOrder = { 'Available': 0, 'In Progress': 1, 'Paused': 2, 'Completed': 3 };

    // Helper: Check if departure time is "relevant" (within -30min to +12hrs from now)
    // Returns: true = Tier 1 (relevant), false = Tier 2 (not relevant)
    const isDepartureRelevant = (departureTime) => {
      if (!departureTime) return true; // No departure time = treat as relevant (don't penalize)

      // Get current time in Eastern
      const now = new Date();
      const etNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

      // Parse departure time (format: "HH:MM" or "HH:MM:SS")
      const [depHour, depMinute] = departureTime.split(':').map(Number);
      const departureDate = new Date(etNow);
      departureDate.setHours(depHour, depMinute, 0, 0);

      // If departure time is before 3am, it's for "tomorrow" in business day terms
      // But for relevance, we compare to actual clock time
      // If departure appears to be in the past by more than 12 hours, it's likely tomorrow
      const diffMs = departureDate - etNow;
      const diffMinutes = diffMs / (1000 * 60);

      // If diff is very negative (like -20 hours), the departure is probably tomorrow
      // Adjust by adding 24 hours
      let adjustedDiffMinutes = diffMinutes;
      if (diffMinutes < -12 * 60) {
        adjustedDiffMinutes = diffMinutes + 24 * 60;
      }

      // Relevant if: within last 30 minutes OR within next 12 hours
      // i.e., -30 minutes <= diff <= +720 minutes (12 hours)
      return adjustedDiffMinutes >= -30 && adjustedDiffMinutes <= 12 * 60;
    };

    filtered.sort((a, b) => {
      let comparison = 0;

      // Default sort: Departure relevance first, then Status, then Store priority, then lines
      if (sortColumn === 'default') {
        // TOP PRIORITY: Departure time relevance (Tier 1 before Tier 2)
        // Only apply to non-completed OBDs
        if (a.status !== 'Completed' && b.status !== 'Completed') {
          const aRelevant = isDepartureRelevant(a.departure_time);
          const bRelevant = isDepartureRelevant(b.departure_time);
          if (aRelevant && !bRelevant) return -1; // a is relevant, b is not -> a first
          if (!aRelevant && bRelevant) return 1;  // b is relevant, a is not -> b first
        }

        // Secondary sort: Status order (Available > In Progress > Paused > Completed)
        const statusA = statusOrder[a.status] ?? 4;
        const statusB = statusOrder[b.status] ?? 4;
        if (statusA !== statusB) {
          return statusA - statusB;
        }

        // Tertiary sort: Store priority (lower number = higher priority)
        const priorityA = STORE_PRIORITY[a.ship_to_code] || DEFAULT_STORE_PRIORITY;
        const priorityB = STORE_PRIORITY[b.ship_to_code] || DEFAULT_STORE_PRIORITY;

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        // Quaternary sort: Total lines (descending - most lines first)
        const linesA = parseInt(a.total_lines) || 0;
        const linesB = parseInt(b.total_lines) || 0;

        if (linesB !== linesA) {
          return linesB - linesA;
        }

        // Final sort: OBD number (ascending - as tiebreaker)
        return (a.obd_number || '').localeCompare(b.obd_number || '');
      }

      // Column-specific sorting
      switch (sortColumn) {
        case 'status':
          comparison = statusOrder[a.status] - statusOrder[b.status];
          break;
        case 'obd_number':
          comparison = (a.obd_number || '').localeCompare(b.obd_number || '');
          break;
        case 'platform':
          comparison = (a.platform || '').localeCompare(b.platform || '');
          break;
        case 'category':
          comparison = (a.category || '').localeCompare(b.category || '');
          break;
        case 'total_items':
          comparison = (a.total_items || 0) - (b.total_items || 0);
          break;
        case 'total_lines':
          comparison = (a.total_lines || 0) - (b.total_lines || 0);
          break;
        case 'ship_to_code':
          comparison = (a.ship_to_code || '').localeCompare(b.ship_to_code || '');
          break;
        case 'departure_time':
          comparison = (a.departure_time || '').localeCompare(b.departure_time || '');
          break;
        case 'assigned_worker':
          comparison = (a.assigned_worker || '').localeCompare(b.assigned_worker || '');
          break;
        default:
          comparison = statusOrder[a.status] - statusOrder[b.status];
      }

      // Secondary sort: if sorting by something other than status, use status as secondary
      if (comparison === 0 && sortColumn !== 'status') {
        comparison = statusOrder[a.status] - statusOrder[b.status];
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    // When a name is selected: float their assigned OBDs to the top,
    // preserving the within-group order that the sort above established.
    if (selectedWorker) {
      const mine      = filtered.filter(o => o.assigned_worker === selectedWorker.name);
      const unassigned = filtered.filter(o => !o.assigned_worker);
      filtered = [...mine, ...unassigned];
    }

    return filtered;
  }, [obds, selectedWorker, platformFilter, platformExclude, statusFilter, statusExclude, categoryFilter, categoryExclude, shipToFilter, shipToExclude, obdSearch, showCompleted, sortColumn, sortDirection]);

  // Render Labor Intelligence Panel
  const renderLaborIntelligence = () => {
    const stats = isHistoricalView ? historicalStats : summary;

    return (
      <Paper sx={{ p: 3, mb: 3, backgroundColor: isHistoricalView ? '#fff8e1' : '#f8f9fa' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isHistoricalView ? (
                <>
                  <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Historical View:{' '}
                  {historicalStart && isValid(historicalStart) ? format(historicalStart, 'MMM d, yyyy h:mm a') : '—'}
                  {' → '}
                  {historicalEnd && isValid(historicalEnd) ? format(historicalEnd, 'MMM d, yyyy h:mm a') : '—'}
                </>
              ) : (
                'Labor Intelligence'
              )}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isHistoricalView}
                  onChange={handleHistoricalToggle}
                  color="warning"
                />
              }
              label={<Typography variant="body2">Historical View</Typography>}
            />
            {isHistoricalView && (
              <>
                <DateTimePicker
                  label="Start Date/Time (ET)"
                  value={historicalStart}
                  onChange={(newValue) => {
                    if (newValue && isValid(newValue)) {
                      setHistoricalStart(newValue);
                    }
                  }}
                  slotProps={{
                    textField: { size: 'small', sx: { width: 220 } },
                    actionBar: { actions: ['clear', 'accept'] }
                  }}
                  ampm={true}
                />
                <DateTimePicker
                  label="End Date/Time (ET)"
                  value={historicalEnd}
                  onChange={(newValue) => {
                    if (newValue && isValid(newValue)) {
                      setHistoricalEnd(newValue);
                    }
                  }}
                  slotProps={{
                    textField: { size: 'small', sx: { width: 220 } },
                    actionBar: { actions: ['clear', 'accept'] }
                  }}
                  ampm={true}
                />
              </>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportDaily}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={2}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e3f2fd', position: 'relative' }}>
              <Tooltip
                title="OBDs can be split into multiple assignments (e.g., Beverages separate from other items)"
                placement="top"
              >
                <InfoOutlinedIcon
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    fontSize: 18,
                    color: 'primary.light',
                    cursor: 'help'
                  }}
                />
              </Tooltip>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                  {stats?.unique_obd_count || stats?.available_count || 0}
                </Typography>
                <Typography variant="h5" color="primary.light" sx={{ fontWeight: 500 }}>
                  / {stats?.available_count || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Typography variant="caption" color="textSecondary">OBDs</Typography>
                <Typography variant="caption" color="textSecondary">/</Typography>
                <Typography variant="caption" color="textSecondary">Assignments</Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#fff3e0' }}>
              <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                {stats?.in_progress_count || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">In Progress</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e3f2fd' }}>
              <Typography variant="h3" color="info.main" sx={{ fontWeight: 700 }}>
                {stats?.paused_count || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">Paused</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e9' }}>
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                {isHistoricalView ? (stats?.completed_count || 0) : (stats?.pending_items || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {isHistoricalView ? 'Completed' : 'Pending Items'}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#f3e5f5' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
                {isHistoricalView ? (stats?.lines_completed || 0) : (stats?.pending_lines || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {isHistoricalView ? 'Lines Completed' : 'Pending Lines'}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#fce4ec' }}>
              <Typography variant="h3" color="secondary.main" sx={{ fontWeight: 700 }}>
                {isHistoricalView ? (stats?.total_items || 0) : (stats?.avg_completion_minutes ? Math.round(stats.avg_completion_minutes) : '-')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {isHistoricalView ? 'Total Items' : 'Avg. Minutes'}
              </Typography>
            </Card>
          </Grid>
          {/* Staffing Card */}
          {currentLabor && (
            <Grid item xs={6} sm={2}>
              <Card sx={{
                textAlign: 'center',
                p: 2,
                backgroundColor: currentLabor.labor_adequate ? '#e8f5e9' : '#fff3e0',
                border: currentLabor.labor_adequate ? '2px solid #4caf50' : '2px solid #ff9800',
                position: 'relative'
              }}>
                {/* Edit button for managers */}
                {canAccessManagerFeatures && (
                  <IconButton
                    size="small"
                    onClick={handleOpenLaborDialog}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      color: currentLabor.labor_adequate ? 'success.main' : 'warning.main'
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                  <GroupIcon sx={{ fontSize: 20, color: currentLabor.labor_adequate ? 'success.main' : 'warning.main' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: currentLabor.labor_adequate ? 'success.main' : 'warning.main' }}>
                  {currentLabor.ft_associates} FT + {currentLabor.pt_associates} PT
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Staffing ({currentLabor.total_labor_hours}h)
                </Typography>
                <Typography variant="caption" sx={{ color: currentLabor.labor_adequate ? 'success.main' : 'warning.main' }}>
                  {currentLabor.labor_adequate ? 'Adequate' : 'Insufficient'}
                </Typography>
              </Card>
            </Grid>
          )}
          {/* Est. Completion Card */}
          {currentLabor && (
            <Grid item xs={6} sm={2}>
              {(() => {
                const isComplete = currentLabor.labor_status === 'complete' || (currentLabor.remaining_work_hours || 0) <= 0;
                const isOvertime = currentLabor.labor_status === 'overtime';
                const noStaff = currentLabor.labor_status === 'no_labor';
                const hasEstimate = currentLabor.estimated_completion && currentLabor.estimated_completion !== 'N/A';

                let bgColor = '#e3f2fd';
                let borderColor = '#1976d2';
                let textColor = 'primary.main';

                if (isComplete) {
                  bgColor = '#e8f5e9';
                  borderColor = '#4caf50';
                  textColor = 'success.main';
                } else if (isOvertime) {
                  bgColor = '#fff3e0';
                  borderColor = '#ff9800';
                  textColor = 'warning.main';
                } else if (noStaff || !hasEstimate) {
                  bgColor = '#ffebee';
                  borderColor = '#f44336';
                  textColor = 'error.main';
                }

                // Build tooltip for overtime explanation
                const capacityBreakdown = currentLabor.capacity_breakdown;
                const overtimeTooltip = isOvertime && capacityBreakdown ? (
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Why overtime?</Typography>
                    <Typography variant="body2">Work needed: {Number(currentLabor.remaining_work_hours || 0).toFixed(1)}h</Typography>
                    <Typography variant="body2">Remaining capacity: {capacityBreakdown.total_remaining_capacity}h</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.75rem', color: '#bbb' }}>
                      ({capacityBreakdown.phase1_hours}h FT+PT + {capacityBreakdown.phase2_hours}h FT only)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                      Overtime: {capacityBreakdown.overtime_hours}h
                    </Typography>
                  </Box>
                ) : '';

                const cardContent = (
                  <Card sx={{
                    textAlign: 'center',
                    p: 2,
                    backgroundColor: bgColor,
                    border: `2px solid ${borderColor}`,
                    cursor: isOvertime ? 'help' : 'default'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                      <AccessTimeIcon sx={{ fontSize: 20, color: textColor }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: textColor }}>
                      {isComplete ? 'Done!' : (currentLabor.estimated_completion || 'N/A')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {isComplete ? 'All Complete' : isOvertime ? 'Est. (Overtime)' : 'Est. Completion'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {Number(currentLabor.remaining_work_hours || 0).toFixed(1)}h remaining
                    </Typography>
                  </Card>
                );

                return isOvertime && capacityBreakdown ? (
                  <Tooltip title={overtimeTooltip} arrow placement="bottom">
                    {cardContent}
                  </Tooltip>
                ) : cardContent;
              })()}
            </Grid>
          )}
        </Grid>

        {/* Overall Progress Bar - Manager View */}
        {canAccessManagerFeatures && platformCompletion?.overall && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                Overall Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {platformCompletion.overall.completed_lines || 0} / {platformCompletion.overall.total_lines || 0} lines
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {platformCompletion.overall.completion_percent || 0}%
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={platformCompletion.overall.completion_percent || 0}
              sx={{
                height: 16,
                borderRadius: 8,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: (platformCompletion.overall.completion_percent || 0) > 75
                    ? '#4caf50'
                    : (platformCompletion.overall.completion_percent || 0) >= 25
                    ? '#ff9800'
                    : '#f44336',
                  borderRadius: 8
                }
              }}
            />
          </Box>
        )}

        {/* Platform Completion Breakdown - Collapsible */}
        {canAccessManagerFeatures && platformCompletion && platformCompletion.platforms && platformCompletion.platforms.length > 0 && (
          <>
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
              onClick={() => setShowPlatformCompletion(!showPlatformCompletion)}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingIcon fontSize="small" />
                Est. Completion by Departure
              </Typography>
              <IconButton size="small">
                {showPlatformCompletion ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={showPlatformCompletion}>
              <TableContainer sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Departure</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Progress</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>OBDs</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Lines</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Work Hours</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Est. Complete</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {platformCompletion.platforms.map((p, idx) => {
                      const completionPercent = p.completion_percent || 0;
                      // Green for >75%, orange for 25-75%, red for <25%
                      const progressColor = completionPercent > 75 ? 'rgba(76, 175, 80, 0.25)' : completionPercent >= 25 ? 'rgba(255, 152, 0, 0.25)' : 'rgba(244, 67, 54, 0.15)';
                      const borderColor = completionPercent > 75 ? '#4caf50' : completionPercent >= 25 ? '#ff9800' : '#f44336';
                      return (
                        <TableRow
                          key={idx}
                          sx={{
                            position: 'relative',
                            // Fill effect: gradient from left to right based on completion
                            background: `linear-gradient(to right, ${progressColor} ${completionPercent}%, transparent ${completionPercent}%)`,
                            borderLeft: `4px solid ${borderColor}`,
                            '&:hover': {
                              background: `linear-gradient(to right, ${progressColor} ${completionPercent}%, rgba(0,0,0,0.04) ${completionPercent}%)`
                            }
                          }}
                        >
                          <TableCell>
                            <Chip
                              label={p.platform || 'Unknown'}
                              size="small"
                              sx={{
                                backgroundColor: PLATFORM_COLORS[p.platform] || '#9e9e9e',
                                color: '#fff',
                                fontWeight: 600
                              }}
                            />
                          </TableCell>
                          <TableCell>{p.departure_time || '-'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 45 }}>
                                {completionPercent}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({p.completed_lines || 0}/{p.total_lines || 0} lines)
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {p.completed_obds || 0}/{p.total_obds || 0}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{p.pending_lines || 0}</TableCell>
                          <TableCell align="right">{(p.work_hours || 0).toFixed(1)}h</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: p.on_track ? 'success.main' : 'error.main'
                              }}
                            >
                              {p.estimated_completion}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={p.on_track ? 'On Track' : 'At Risk'}
                              size="small"
                              color={p.on_track ? 'success' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </>
        )}
      </Paper>
    );
  };

  // Render Worker Selection
  const renderWorkerSelection = () => (
    <Paper sx={{
      p: 2,
      mb: 2,
      position: 'sticky',
      top: 0,
      zIndex: 100,
      backgroundColor: selectedWorker ? '#e3f2fd' : '#fff',
      borderLeft: selectedWorker ? '4px solid #1976d2' : 'none',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={isAssociate ? 6 : 4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Autocomplete
              options={workers}
              getOptionLabel={(option) => option.name}
              value={selectedWorker}
              onChange={(e, newValue) => setSelectedWorker(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={isAssociate ? "Select Your Name" : "Select Associate"}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: selectedWorker ? '#fff' : 'transparent'
                    }
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ minWidth: 250, flexGrow: 1 }}
            />
            {autoAssignEnabled && isAssociate && (
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                onClick={() => selectedWorker && requestVerification('startNext')}
                disabled={!selectedWorker}
                sx={{ height: 56, whiteSpace: 'nowrap' }}
              >
                Start my next OBD
              </Button>
            )}
            {selectedWorker && (
              <Box sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                backgroundColor: '#1976d2',
                borderRadius: 2
              }}>
                <PersonIcon sx={{ color: '#fff' }} />
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                  {selectedWorker.name}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* These controls are only visible to managers/admins */}
        {canAccessManagerFeatures && (
          <>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PersonIcon />}
                onClick={() => selectedWorker && handleOpenAssociateReport(selectedWorker)}
                disabled={!selectedWorker}
                sx={{ height: 56 }}
              >
                View Report
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setAddWorkerDialogOpen(true)}
                sx={{ height: 56 }}
              >
                Add Associate
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant={showFilters ? "contained" : "outlined"}
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ height: 56 }}
                color={activeFilterCount > 0 ? "primary" : "inherit"}
              >
                Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
              </Button>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <Tooltip title="Save current filters as default view for an associate">
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={() => setSetViewDialogOpen(true)}
                  sx={{ height: 56, fontSize: '0.75rem' }}
                >
                  Set View
                </Button>
              </Tooltip>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <Tooltip title="View the page as a specific associate sees it">
                <Button
                  fullWidth
                  variant={viewingAsAssociate ? "contained" : "outlined"}
                  color={viewingAsAssociate ? "secondary" : "inherit"}
                  startIcon={<VisibilityIcon />}
                  onClick={() => setViewAsDialogOpen(true)}
                  sx={{ height: 56, fontSize: '0.75rem' }}
                >
                  {viewingAsAssociate ? `As: ${viewingAsAssociate}` : 'View As'}
                </Button>
              </Tooltip>
            </Grid>

            {/* Visual indicator for associates with custom views */}
            {allViewSettings.length > 0 && (
              <Grid item xs={12} md={3}>
                <Alert
                  severity="warning"
                  sx={{
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#fff3e0' }
                  }}
                  onClick={() => setViewSettingsDetailOpen(true)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>{allViewSettings.length}</strong> associate{allViewSettings.length !== 1 ? 's have' : ' has'} custom filters
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      (click for details)
                    </Typography>
                  </Box>
                </Alert>
              </Grid>
            )}

            {/* Reset All Views button - only show if there are custom views */}
            {allViewSettings.length > 0 && (
              <Grid item xs={6} md={1.5}>
                <Tooltip title="Clear all custom filters for all associates">
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<ClearIcon />}
                    onClick={() => setResetViewsDialogOpen(true)}
                    sx={{ height: 56, fontSize: '0.7rem' }}
                  >
                    Reset All Views
                  </Button>
                </Tooltip>
              </Grid>
            )}
          </>
        )}

        <Grid item xs={12} md={isAssociate ? 6 : 1.5}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchAllOBDs(); fetchSummary(); }}
            sx={{ height: 56 }}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>

      {/* Expandable Filters Section - only for managers/admins */}
      {canAccessManagerFeatures && <Collapse in={showFilters}>
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            {/* Platform Filter - Multi-select with exclude toggle */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <Tooltip title={platformExclude ? "Excluding selected (click to include)" : "Including selected (click to exclude)"}>
                <IconButton
                  size="small"
                  onClick={() => setPlatformExclude(!platformExclude)}
                  sx={{
                    mt: 1,
                    color: platformExclude ? 'error.main' : 'success.main',
                    '&:hover': { backgroundColor: platformExclude ? 'error.light' : 'success.light' }
                  }}
                >
                  {platformExclude ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                </IconButton>
              </Tooltip>
              <Autocomplete
                multiple
                size="small"
                options={platforms}
                value={platformFilter}
                onChange={(e, newValue) => setPlatformFilter(newValue)}
                sx={{ minWidth: 180 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={platformExclude ? "Exclude Platform" : "Platform"}
                    placeholder="Select..."
                    sx={{
                      '& .MuiOutlinedInput-root': platformExclude ? {
                        '& fieldset': { borderColor: 'error.main' }
                      } : {}
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      size="small"
                      color={platformExclude ? "error" : "primary"}
                    />
                  ))
                }
              />
            </Box>

            {/* Status Filter - Multi-select with exclude toggle */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <Tooltip title={statusExclude ? "Excluding selected (click to include)" : "Including selected (click to exclude)"}>
                <IconButton
                  size="small"
                  onClick={() => setStatusExclude(!statusExclude)}
                  sx={{
                    mt: 1,
                    color: statusExclude ? 'error.main' : 'success.main',
                    '&:hover': { backgroundColor: statusExclude ? 'error.light' : 'success.light' }
                  }}
                >
                  {statusExclude ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                </IconButton>
              </Tooltip>
              <Autocomplete
                multiple
                size="small"
                options={['Available', 'In Progress', 'Paused', 'Completed']}
                value={statusFilter}
                onChange={(e, newValue) => setStatusFilter(newValue)}
                sx={{ minWidth: 180 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={statusExclude ? "Exclude Status" : "Status"}
                    placeholder="Select..."
                    sx={{
                      '& .MuiOutlinedInput-root': statusExclude ? {
                        '& fieldset': { borderColor: 'error.main' }
                      } : {}
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      size="small"
                      color={statusExclude ? "error" : "primary"}
                    />
                  ))
                }
              />
            </Box>

            {/* Category Filter - Multi-select with exclude toggle */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <Tooltip title={categoryExclude ? "Excluding selected (click to include)" : "Including selected (click to exclude)"}>
                <IconButton
                  size="small"
                  onClick={() => setCategoryExclude(!categoryExclude)}
                  sx={{
                    mt: 1,
                    color: categoryExclude ? 'error.main' : 'success.main',
                    '&:hover': { backgroundColor: categoryExclude ? 'error.light' : 'success.light' }
                  }}
                >
                  {categoryExclude ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                </IconButton>
              </Tooltip>
              <Autocomplete
                multiple
                size="small"
                options={uniqueCategories}
                value={categoryFilter}
                onChange={(e, newValue) => setCategoryFilter(newValue)}
                sx={{ minWidth: 220 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={categoryExclude ? "Exclude Category" : "Category"}
                    placeholder="Select..."
                    sx={{
                      '& .MuiOutlinedInput-root': categoryExclude ? {
                        '& fieldset': { borderColor: 'error.main' }
                      } : {}
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      size="small"
                      color={categoryExclude ? "error" : "primary"}
                    />
                  ))
                }
              />
            </Box>

            {/* Ship-To Filter - Multi-select with exclude toggle */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flexGrow: 1, maxWidth: 400 }}>
              <Tooltip title={shipToExclude ? "Excluding selected (click to include)" : "Including selected (click to exclude)"}>
                <IconButton
                  size="small"
                  onClick={() => setShipToExclude(!shipToExclude)}
                  sx={{
                    mt: 1,
                    color: shipToExclude ? 'error.main' : 'success.main',
                    '&:hover': { backgroundColor: shipToExclude ? 'error.light' : 'success.light' }
                  }}
                >
                  {shipToExclude ? <RemoveCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                </IconButton>
              </Tooltip>
              <Autocomplete
                multiple
                size="small"
                options={uniqueShipTos}
                value={shipToFilter}
                onChange={(e, newValue) => setShipToFilter(newValue)}
                sx={{ minWidth: 250, flexGrow: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={shipToExclude ? "Exclude Ship-To" : "Ship-To"}
                    placeholder="Select stores..."
                    sx={{
                      '& .MuiOutlinedInput-root': shipToExclude ? {
                        '& fieldset': { borderColor: 'error.main' }
                      } : {}
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      size="small"
                      color={shipToExclude ? "error" : "primary"}
                    />
                  ))
                }
              />
            </Box>

            <Button
              variant="text"
              onClick={clearFilters}
              disabled={activeFilterCount === 0}
              sx={{ minWidth: 120, mt: 1 }}
            >
              Clear Filters
            </Button>
          </Box>

          {/* Active Filters Display */}
          {activeFilterCount > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {platformFilter.length > 0 && (
                <Chip
                  label={`${platformExclude ? 'NOT ' : ''}Platform: ${platformFilter.length === 1 ? platformFilter[0] : `${platformFilter.length} selected`}`}
                  onDelete={() => setPlatformFilter([])}
                  size="small"
                  color={platformExclude ? "error" : "primary"}
                />
              )}
              {statusFilter.length > 0 && (
                <Chip
                  label={`${statusExclude ? 'NOT ' : ''}Status: ${statusFilter.length === 1 ? statusFilter[0] : `${statusFilter.length} selected`}`}
                  onDelete={() => setStatusFilter([])}
                  size="small"
                  color={statusExclude ? "error" : "primary"}
                />
              )}
              {categoryFilter.length > 0 && (
                <Chip
                  label={`${categoryExclude ? 'NOT ' : ''}Category: ${categoryFilter.length === 1 ? categoryFilter[0] : `${categoryFilter.length} selected`}`}
                  onDelete={() => setCategoryFilter([])}
                  size="small"
                  color={categoryExclude ? "error" : "primary"}
                />
              )}
              {shipToFilter.length > 0 && (
                <Chip
                  label={`${shipToExclude ? 'NOT ' : ''}Ship-To: ${shipToFilter.length === 1 ? shipToFilter[0] : `${shipToFilter.length} selected`}`}
                  onDelete={() => setShipToFilter([])}
                  size="small"
                  color={shipToExclude ? "error" : "primary"}
                />
              )}
            </Box>
          )}
        </Box>
      </Collapse>}

      {/* Active OBD for selected worker */}
      {selectedWorker && getActiveOBDsForWorker().length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body1">
              <strong>{selectedWorker.name}</strong> is currently working on:
            </Typography>
          </Alert>
          {getActiveOBDsForWorker().map(obd => (
            <Card key={obd.id} sx={{
              p: 2,
              mb: 2,
              border: obd.status === 'Paused' ? '2px solid #2196f3' : '2px solid #ff9800',
              backgroundColor: obd.status === 'Paused' ? '#e3f2fd' : '#fff8e1'
            }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    OBD {obd.obd_number}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {getPlatformChip(obd.platform)} {getCategoryChip(obd.category_subset || obd.category)} {getStatusChip(obd.status, obd.auto_completed)}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="textSecondary">Items / Lines</Typography>
                  <Typography variant="h6">{obd.total_items} / {obd.total_lines}</Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="body2" color="textSecondary">Timer</Typography>
                  <Typography
                    variant="h4"
                    color={obd.status === 'Paused' ? 'info.main' : 'warning.main'}
                    sx={{ fontWeight: 700, fontFamily: 'monospace' }}
                  >
                    {obd.status === 'Paused' ? 'PAUSED' : calculateLiveDuration(obd.start_time, obd.total_paused_seconds, obd.pause_start)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {obd.status === 'In Progress' && (
                      <>
                        {/* Pause is lead-only (client request) — associates ask a lead,
                            who pauses from the Activity Monitor Manager Override */}
                        {!isAssociate && (
                          <Button
                            fullWidth
                            variant="outlined"
                            color="info"
                            size="large"
                            startIcon={<PauseIcon />}
                            onClick={() => handlePauseOBD(obd.id)}
                            sx={{ py: 2 }}
                          >
                            Pause
                          </Button>
                        )}
                        <Button
                          fullWidth
                          variant="contained"
                          color="success"
                          size="large"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleCompleteOBD(obd.id)}
                          sx={{ py: 2 }}
                        >
                          Complete
                        </Button>
                      </>
                    )}
                    {obd.status === 'Paused' && (
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleResumeOBD(obd.id)}
                        sx={{ py: 2 }}
                      >
                        Resume
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>
      )}
    </Paper>
  );

  // Render OBD Table
  const renderOBDTable = () => {
    // In cross-history search mode, use search results instead of the filtered view
    const isHistorySearchMode = historySearchResults !== null;
    const displayRows = isHistorySearchMode ? historySearchResults : filteredAndSortedOBDs;

    const completedCount = obds.filter(o => o.status === 'Completed').length;

    // Count unique OBD numbers vs total assignments (including category splits)
    const uniqueObdCount = new Set(displayRows.map(o => o.obd_number)).size;
    const totalAssignments = displayRows.length;

    // Gate: does the selected worker still have any incomplete assigned OBDs?
    // If yes, the Start button on unassigned OBDs will be disabled with a tooltip.
    // Uses the full obds array (pre-filter) so assignments hidden by other filters
    // still block unassigned starts.
    const hasIncompleteAssigned = selectedWorker
      ? obds.some(o =>
          o.assigned_worker === selectedWorker.name &&
          ['Available', 'In Progress', 'Paused'].includes(o.status)
        )
      : false;

    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isHistorySearchMode
              ? `History Search Results (${uniqueObdCount} OBDs)`
              : `OBD Sign-On Sheet (${uniqueObdCount} OBDs / ${totalAssignments} Assignments)`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {sortColumn !== 'default' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleResetSort}
              >
                Reset to Default Sort
              </Button>
            )}
            {completedCount > 0 && (
              <Button
                size="small"
                startIcon={showCompleted ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowCompleted(!showCompleted)}
              >
                {showCompleted ? 'Hide' : 'Show'} Completed ({completedCount})
              </Button>
            )}
          </Box>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* Multi-select info bar - shows selection status, no start buttons here */}
        {selectedOBDs.length > 0 && (
          <Box sx={{
            mb: 2, p: 1.5,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}>
            {(() => {
              const selectedObdList = obds.filter(o => selectedOBDs.includes(o.id));
              const shipToCodes = [...new Set(selectedObdList.map(o => o.ship_to_code))];
              const allSameStore = shipToCodes.length === 1;
              const categories = [...new Set(selectedObdList.map(o => o.category))];
              const allSameCategory = categories.length === 1;
              const totalLines = selectedObdList.reduce((sum, o) => sum + (o.total_lines || 0), 0);
              const differentStores = selectedOBDs.length >= 2 && !allSameStore;
              const differentCategories = selectedOBDs.length >= 2 && !allSameCategory;

              return (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedOBDs.length} OBD{selectedOBDs.length > 1 ? 's' : ''} selected
                    {allSameStore && selectedOBDs.length > 1 && ` • ${totalLines} lines • Store ${shipToCodes[0]}`}
                  </Typography>

                  {differentStores && differentCategories && (
                    <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                      ⚠ Different stores and categories selected - select same store and category to start together
                    </Typography>
                  )}

                  {differentStores && !differentCategories && (
                    <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                      ⚠ Different stores selected - select same store to start together
                    </Typography>
                  )}

                  {!differentStores && differentCategories && (
                    <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                      ⚠ Different categories selected - select same category to start together
                    </Typography>
                  )}

                  {canAccessManagerFeatures && (
                    <Tooltip title={selectedObdList.some(o => o.status !== 'Available') ? 'Only Available OBDs can be assigned' : ''}>
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<PersonAddIcon />}
                          onClick={() => setBulkAssignDialogOpen(true)}
                          disabled={selectedObdList.some(o => o.status !== 'Available')}
                        >
                          Assign Selected ({selectedOBDs.length})
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearSelection}
                  >
                    Clear
                  </Button>

                  {canAccessManagerFeatures && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => setBulkDeleteDialogOpen(true)}
                    >
                      Delete Selected ({selectedOBDs.length})
                    </Button>
                  )}
                </>
              );
            })()}
          </Box>
        )}

        {/* Scrollable table with always-visible horizontal scrollbar */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            '& .MuiTableContainer-root': {
              overflowX: 'auto',
              overflowY: 'visible',
              // Always show scrollbar (not just on hover)
              '&::-webkit-scrollbar': {
                height: 12,
                display: 'block'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: 6
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: 6,
                '&:hover': {
                  backgroundColor: '#555'
                }
              },
              // Firefox
              scrollbarWidth: 'auto',
              scrollbarColor: '#888 #f1f1f1'
            }
          }}
        >
          {/* Sticky scrollbar container at bottom of viewport */}
          <TableContainer
            sx={{
              maxHeight: 'calc(100vh - 350px)',
              position: 'relative',
              // Sticky horizontal scrollbar
              '& table': {
                minWidth: 1200
              }
            }}
          >
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { backgroundColor: '#f5f5f5' } }}>
                {/* Checkbox column — hidden in history search mode */}
                {!isHistorySearchMode && (
                  <TableCell padding="checkbox" sx={{ width: 50 }}>
                    <Checkbox
                      indeterminate={selectedOBDs.length > 0 && selectedOBDs.length < filteredAndSortedOBDs.filter(o => o.status === 'Available').length}
                      checked={filteredAndSortedOBDs.filter(o => o.status === 'Available').length > 0 && selectedOBDs.length === filteredAndSortedOBDs.filter(o => o.status === 'Available').length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleSelectAll(filteredAndSortedOBDs.filter(o => o.status === 'Available').map(o => o.id));
                        } else {
                          handleClearSelection();
                        }
                      }}
                      size="small"
                    />
                  </TableCell>
                )}
                {/* Action column — hidden in history search mode */}
                {!isHistorySearchMode && (
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                )}
                <TableCell sx={{ fontWeight: 600 }}>
                  {canAccessManagerFeatures ? (
                    <TableSortLabel
                      active={sortColumn === 'obd_number'}
                      direction={sortColumn === 'obd_number' ? sortDirection : 'asc'}
                      onClick={() => handleSort('obd_number')}
                    >
                      OBD Number
                    </TableSortLabel>
                  ) : 'OBD Number'}
                </TableCell>
                {/* Date column — only shown in history search mode */}
                {isHistorySearchMode && (
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                )}
                <TableCell sx={{ fontWeight: 600 }}>
                  {canAccessManagerFeatures ? (
                    <TableSortLabel
                      active={sortColumn === 'assigned_worker'}
                      direction={sortColumn === 'assigned_worker' ? sortDirection : 'asc'}
                      onClick={() => handleSort('assigned_worker')}
                    >
                      Associate
                    </TableSortLabel>
                  ) : 'Associate'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {canAccessManagerFeatures ? (
                    <TableSortLabel
                      active={sortColumn === 'category'}
                      direction={sortColumn === 'category' ? sortDirection : 'asc'}
                      onClick={() => handleSort('category')}
                    >
                      Category
                    </TableSortLabel>
                  ) : 'Category'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {canAccessManagerFeatures ? (
                    <TableSortLabel
                      active={sortColumn === 'platform'}
                      direction={sortColumn === 'platform' ? sortDirection : 'asc'}
                      onClick={() => handleSort('platform')}
                    >
                      Platform
                    </TableSortLabel>
                  ) : 'Platform'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {canAccessManagerFeatures ? (
                    <TableSortLabel
                      active={sortColumn === 'ship_to_code'}
                      direction={sortColumn === 'ship_to_code' ? sortDirection : 'asc'}
                      onClick={() => handleSort('ship_to_code')}
                    >
                      Ship-To
                    </TableSortLabel>
                  ) : 'Ship-To'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {canAccessManagerFeatures ? (
                    <TableSortLabel
                      active={sortColumn === 'status'}
                      direction={sortColumn === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  ) : 'Status'}
                </TableCell>
                {/* Age — hidden in history search mode (band is calculated vs. *today's*
                    business day, which would mislabel rows from other days). */}
                {!isHistorySearchMode && (
                  <TableCell sx={{ fontWeight: 600 }}>Age</TableCell>
                )}
                {!isHistorySearchMode && (
                  <TableCell sx={{ fontWeight: 600 }}>
                    {canAccessManagerFeatures ? (
                      <TableSortLabel
                        active={sortColumn === 'departure_time'}
                        direction={sortColumn === 'departure_time' ? sortDirection : 'asc'}
                        onClick={() => handleSort('departure_time')}
                      >
                        Departs In
                      </TableSortLabel>
                    ) : 'Departs In'}
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600 }}>
                  {canAccessManagerFeatures ? (
                    <TableSortLabel
                      active={sortColumn === 'total_lines'}
                      direction={sortColumn === 'total_lines' ? sortDirection : 'asc'}
                      onClick={() => handleSort('total_lines')}
                    >
                      Lines
                    </TableSortLabel>
                  ) : 'Lines'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {canAccessManagerFeatures ? (
                    <TableSortLabel
                      active={sortColumn === 'total_items'}
                      direction={sortColumn === 'total_items' ? sortDirection : 'asc'}
                      onClick={() => handleSort('total_items')}
                    >
                      Items
                    </TableSortLabel>
                  ) : 'Items'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                {/* Wall-clock columns — historical/history-search modes only */}
                {isHistoricalView && (
                  <>
                    <TableCell sx={{ fontWeight: 600 }}>Elapsed</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Pause</TableCell>
                  </>
                )}
                <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.length === 0 && !loading && !historySearchLoading ? (
                <TableRow>
                  {/* colSpan: normal=14 cols (incl. new Age col), history search=11 cols (no checkbox, no action, no age, no departs; +date);
                      historical view adds 2 wall-clock cols (Elapsed, Pause) to either mode */}
                  <TableCell colSpan={(isHistorySearchMode ? 11 : 14) + (isHistoricalView ? 2 : 0)} sx={{ textAlign: 'center', py: 6 }}>
                    <LocalShippingIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      {isHistorySearchMode ? `No OBDs found matching "${obdSearch}" in history` : 'No OBDs available'}
                    </Typography>
                    {!isHistorySearchMode && (
                      <Typography variant="body2" color="textSecondary">
                        {activeFilterCount > 0
                          ? 'Try adjusting your filters'
                          : 'Import OBDs from SAP Fiori using the Manager View'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                displayRows.map((obd) => {
                  const timeUntilDeparture = getTimeUntilDeparture(obd.departure_time);
                  const isUrgent = timeUntilDeparture && !timeUntilDeparture.includes('h') && parseInt(timeUntilDeparture) < 60;
                  const isMyOBD = selectedWorker && obd.assigned_worker === selectedWorker.name;
                  // True when a worker is selected, this OBD is unassigned, and they still
                  // have at least one incomplete assigned OBD they should finish first.
                  const isUnassignedGated = selectedWorker && !obd.assigned_worker && hasIncompleteAssigned;
                  const duration = obd.status === 'In Progress'
                    ? calculateLiveDuration(obd.start_time, obd.total_paused_seconds, obd.pause_start)
                    : obd.status === 'Paused'
                    ? calculateLiveDuration(obd.start_time, obd.total_paused_seconds, obd.pause_start)
                    : obd.status === 'Completed'
                      ? (obd.auto_completed ? '—' : calculateDuration(obd.start_time, obd.end_time, obd.total_paused_seconds))
                      : '-';

                  return (
                    <TableRow
                      key={obd.id}
                      sx={{
                        backgroundColor:
                          selectedAssignments.includes(obd.id)
                            ? '#e3f2fd'
                            : obd.status === 'In Progress'
                              ? isMyOBD ? '#fff3e0' : '#fafafa'
                              : obd.status === 'Paused'
                                ? isMyOBD ? '#e3f2fd' : '#f5f5f5'
                                : obd.status === 'Available'
                                  ? isUrgent ? '#ffebee' : '#e8f5e9'
                                  : obd.status === 'Completed'
                                    ? '#f5f5f5'
                                    : 'inherit',
                        '&:hover': { backgroundColor: '#f0f0f0' },
                        opacity: obd.status === 'Completed' ? 0.7 : 1
                      }}
                    >
                      {/* Checkbox for multi-select — hidden in history search mode */}
                      {!isHistorySearchMode && (
                        <TableCell padding="checkbox">
                          {obd.status === 'Available' && !(autoAssignEnabled && isAssociate) && !cantPickObd(obd) && (
                            <Checkbox
                              checked={selectedOBDs.includes(obd.id)}
                              onChange={() => handleToggleSelect(obd.id)}
                              size="small"
                            />
                          )}
                        </TableCell>
                      )}
                      {/* Action column — hidden in history search mode */}
                      {!isHistorySearchMode && (<TableCell>
                        {obd.status === 'Available' ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {(() => {
                            // "Can't pick" (people restriction): no Start for
                            // the selected associate's excluded categories
                            if (cantPickObd(obd)) {
                              return (
                                <Typography variant="caption" sx={{ color: '#98A2B3' }}>
                                  Can&apos;t pick — see your lead
                                </Typography>
                              );
                            }
                            // Determine button state based on selection
                            const selectedObdList = obds.filter(o => selectedOBDs.includes(o.id));
                            const shipToCodes = [...new Set(selectedObdList.map(o => o.ship_to_code))];
                            const categories = [...new Set(selectedObdList.map(o => o.category))];
                            const hasMultipleSelected = selectedOBDs.length >= 2;
                            const allSameStore = shipToCodes.length === 1;
                            const allSameCategory = categories.length === 1;
                            const differentStoresSelected = hasMultipleSelected && !allSameStore;
                            const differentCategoriesSelected = hasMultipleSelected && !allSameCategory;
                            const isThisRowSelected = selectedOBDs.includes(obd.id);
                            const multiSameStoreSelected = hasMultipleSelected && allSameStore && allSameCategory;

                            // Different stores or categories selected = ALL buttons greyed out
                            if (differentStoresSelected || differentCategoriesSelected) {
                              return (
                                <Tooltip title="Clear selection or select same store and category only">
                                  <span>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      size="small"
                                      startIcon={<PlayArrowIcon />}
                                      disabled
                                    >
                                      Start
                                    </Button>
                                  </span>
                                </Tooltip>
                              );
                            }

                            // Multiple same-store selected, this row IS selected = "Start All"
                            if (multiSameStoreSelected && isThisRowSelected) {
                              if (isUnassignedGated) {
                                return (
                                  <Tooltip title="Complete your assigned OBDs first.">
                                    <span>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<PlayArrowIcon />}
                                        disabled
                                      >
                                        Start All
                                      </Button>
                                    </span>
                                  </Tooltip>
                                );
                              }
                              return (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  startIcon={<PlayArrowIcon />}
                                  onClick={handleMultiOBDClaim}
                                  disabled={!selectedWorker}
                                >
                                  Start All
                                </Button>
                              );
                            }

                            // Multiple same-store selected, this row NOT selected = greyed out
                            if (multiSameStoreSelected && !isThisRowSelected) {
                              return (
                                <Tooltip title="This OBD is not selected">
                                  <span>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      size="small"
                                      startIcon={<PlayArrowIcon />}
                                      disabled
                                    >
                                      Start
                                    </Button>
                                  </span>
                                </Tooltip>
                              );
                            }

                            // No multi-select or single selected = normal Start.
                            // Gate unassigned OBDs when the worker has incomplete assigned ones.
                            if (isUnassignedGated) {
                              return (
                                <Tooltip title="Complete your assigned OBDs first.">
                                  <span>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      size="small"
                                      startIcon={<PlayArrowIcon />}
                                      disabled
                                    >
                                      Start
                                    </Button>
                                  </span>
                                </Tooltip>
                              );
                            }
                            // Auto-assignment: associates get OBDs from the
                            // engine — the list is read-only for them.
                            if (autoAssignEnabled && isAssociate) return null;
                            return (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<PlayArrowIcon />}
                                onClick={() => handleClaimOBD(obd.id)}
                                disabled={!selectedWorker}
                              >
                                Start
                              </Button>
                            );
                          })()}
                          {canAccessManagerFeatures && (
                            <Tooltip title={obd.assigned_worker ? `Pre-assigned to ${obd.assigned_worker} — click to change` : 'Pre-assign this OBD to an associate'}>
                              <Button
                                variant="outlined"
                                color={obd.assigned_worker ? 'warning' : 'secondary'}
                                size="small"
                                startIcon={obd.assigned_worker ? <SwapHorizIcon /> : <PersonAddIcon />}
                                onClick={() => {
                                  setPreassignTargetObd(obd);
                                  setPreassignDialogOpen(true);
                                }}
                              >
                                {obd.assigned_worker ? 'Change' : 'Assign'}
                              </Button>
                            </Tooltip>
                          )}
                          </Box>
                        ) : obd.status === 'In Progress' && isMyOBD ? (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {/* Pause is lead-only (client request) */}
                            {!isAssociate && (
                              <Button
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<PauseIcon />}
                                onClick={() => handlePauseOBD(obd.id)}
                              >
                                Pause
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleCompleteOBD(obd.id)}
                            >
                              Complete
                            </Button>
                          </Box>
                        ) : obd.status === 'Paused' && isMyOBD ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => handleResumeOBD(obd.id)}
                          >
                            Resume
                          </Button>
                        ) : (obd.status === 'In Progress' || obd.status === 'Paused') ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {/* Manager actions for assigned OBDs */}
                            {canAccessManagerFeatures && (
                              <>
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  startIcon={<SwapHorizIcon />}
                                  onClick={() => {
                                    setReassignTargetObd(obd);
                                    setReassignDialogOpen(true);
                                  }}
                                >
                                  Reassign
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<PersonRemoveIcon />}
                                  onClick={() => handleUnassignOBD(obd.id)}
                                >
                                  Unassign
                                </Button>
                              </>
                            )}
                            {!canAccessManagerFeatures && (
                              <Typography variant="caption" color="textSecondary">
                                {obd.assigned_worker}
                              </Typography>
                            )}
                          </Box>
                        ) : obd.status === 'Completed' && canAccessManagerFeatures ? (
                          <Tooltip title="Reset OBD back to Available">
                            <Button
                              variant="outlined"
                              color="warning"
                              size="small"
                              startIcon={<RestartAltIcon />}
                              onClick={() => {
                                setResetTargetObd(obd);
                                setResetDialogOpen(true);
                              }}
                            >
                              Reset
                            </Button>
                          </Tooltip>
                        ) : null}
                      </TableCell>)}
                      <TableCell sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                        <Button
                          variant="text"
                          onClick={() => handleOpenDetails(obd)}
                          sx={{
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            textTransform: 'none',
                            p: 0,
                            minWidth: 'auto',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {obd.obd_number}
                        </Button>
                      </TableCell>
                      {/* Date column — only shown in history search mode */}
                      {isHistorySearchMode && (
                        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                          {obd.created_at
                            ? new Date(obd.created_at).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric',
                                timeZone: 'America/New_York'
                              })
                            : '-'}
                        </TableCell>
                      )}
                      <TableCell>
                        {obd.assigned_worker || '-'}
                      </TableCell>
                      <TableCell>{getCategoryChip(obd.category_subset || obd.category)}</TableCell>
                      <TableCell>{getPlatformChip(obd.platform)}</TableCell>
                      <TableCell>{obd.store_display_name || obd.ship_to_code}</TableCell>
                      <TableCell>{getStatusChip(obd.status, obd.auto_completed)}</TableCell>
                      {/* Age Chip — sap_created_on persisted from SAP CREATEDON.
                          NULL (legacy rows + manual Fiori imports) renders as blank. */}
                      {!isHistorySearchMode && (
                        <TableCell>
                          {(() => {
                            const band = getAgeBand(obd.sap_created_on);
                            if (!band) return null;
                            return (
                              <Chip
                                label={AGE_BAND_LABELS[band]}
                                size="small"
                                sx={{
                                  backgroundColor: AGE_BAND_COLORS[band],
                                  color: '#fff',
                                  fontWeight: 600,
                                  minWidth: 56,
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                      )}
                      {/* Departs In — hidden in history search mode (departure times aren't relevant for history) */}
                      {!isHistorySearchMode && (
                        <TableCell>
                          {timeUntilDeparture ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {isUrgent && <WarningIcon sx={{ color: '#f44336', fontSize: 18 }} />}
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isUrgent ? '#f44336' : 'textSecondary',
                                  fontWeight: isUrgent ? 600 : 'normal'
                                }}
                              >
                                {timeUntilDeparture}
                              </Typography>
                            </Box>
                          ) : '-'}
                        </TableCell>
                      )}
                      <TableCell>{obd.total_lines}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{obd.total_items}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {duration}
                      </TableCell>
                      {/* Wall-clock columns — Elapsed is gross start→finish (no pause
                          subtraction), Pause is accumulated paused minutes */}
                      {isHistoricalView && (
                        <>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {obd.status === 'Completed' && obd.start_time && obd.end_time && !obd.auto_completed
                              ? calculateDuration(obd.start_time, obd.end_time, 0)
                              : '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {obd.status === 'Completed' && obd.start_time && obd.end_time && !obd.auto_completed
                              ? `${Math.round((obd.total_paused_seconds || 0) / 60)}m`
                              : '-'}
                          </TableCell>
                        </>
                      )}
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {obd.status === 'Completed' && obd.end_time ? formatTime(obd.end_time) : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      </Paper>
    );
  };

  // Render Manager View (role-based access - no password needed)
  const renderManagerView = () => (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Import OBDs from SAP Fiori
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Paste the raw data from SAP Fiori Item View below. The system will automatically:
          <ul>
            <li>Parse OBD numbers, SKUs, quantities, and ship-to codes</li>
            <li>Categorize OBDs by product type (Beverages, Snacks, HBA, etc.)</li>
            <li>Map ship-to codes to platforms</li>
            <li>Set departure times from Configuration</li>
            <li><strong>Detect and skip duplicate OBDs</strong></li>
          </ul>
        </Typography>

            <TextField
              fullWidth
              multiline
              rows={10}
              variant="outlined"
              placeholder={`Paste SAP Fiori data here...

Example format:
|     ShPt     Volume VUn                                                       |
|     Delivery   Material   Item Description               Delivery Q Ship-to   |
|     W910                                                                      |
|     81938343   1001397    KIND DK CH ALM COC                     27  1211      |
|     81938343   1005734    KS REESES PB CUP                        4  1211      |`}
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.85rem' }}
            />

            {/* Labor Assignment Section */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon color="primary" />
                Labor Assignment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Assign associates to calculate if you have adequate staffing for today's workload.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Full-Time Associates"
                  type="number"
                  value={ftAssociates}
                  onChange={(e) => setFtAssociates(Math.max(0, parseInt(e.target.value) || 0))}
                  InputProps={{ inputProps: { min: 0, max: 50 } }}
                  helperText="8-hour shifts"
                  size="small"
                  sx={{ width: 160 }}
                />

                <TextField
                  label="Part-Time Associates"
                  type="number"
                  value={ptAssociates}
                  onChange={(e) => setPtAssociates(Math.max(0, parseInt(e.target.value) || 0))}
                  InputProps={{ inputProps: { min: 0, max: 50 } }}
                  helperText="4-hour shifts"
                  size="small"
                  sx={{ width: 160 }}
                />

                <TextField
                  label="Shift Start Time"
                  type="time"
                  value={shiftStartTime}
                  onChange={(e) => setShiftStartTime(e.target.value)}
                  helperText="When shift begins"
                  size="small"
                  sx={{ width: 140 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              {/* Show calculation preview */}
              {(ftAssociates > 0 || ptAssociates > 0) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Available Labor:</strong> {(ftAssociates * 8) + (ptAssociates * 4)} hours
                    ({ftAssociates} FT × 8h + {ptAssociates} PT × 4h)
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Shift:</strong> {new Date(`2000-01-01T${shiftStartTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                    {ftAssociates > 0 && ` → FT until ${new Date(new Date(`2000-01-01T${shiftStartTime}`).getTime() + 8*60*60*1000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                    {ptAssociates > 0 && ` • PT until ${new Date(new Date(`2000-01-01T${shiftStartTime}`).getTime() + 4*60*60*1000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                  </Typography>
                </Alert>
              )}
            </Box>

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleImport}
              disabled={importing || !importData.trim()}
              startIcon={importing ? <CircularProgress size={20} /> : <LocalShippingIcon />}
              sx={{ mr: 2 }}
            >
              {importing ? 'Importing...' : 'Import OBDs'}
            </Button>

            <Button
              variant="outlined"
              onClick={() => { setImportData(''); setImportResult(null); }}
            >
              Clear
            </Button>
          </Paper>

          {importResult && (
            <Paper sx={{ p: 3, backgroundColor: importResult.duplicatesSkipped > 0 ? '#fff3e0' : '#e8f5e9' }}>
              <Typography variant="h6" color={importResult.duplicatesSkipped > 0 ? 'warning.main' : 'success.main'} gutterBottom>
                Import {importResult.duplicatesSkipped > 0 ? 'Completed with Warnings' : 'Successful'}!
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body1">
                    <strong>OBDs Imported:</strong> {importResult.summary.totalObds}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body1">
                    <strong>Total Items:</strong> {importResult.summary.totalItems}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body1">
                    <strong>Import ID:</strong> {importResult.import.id}
                  </Typography>
                </Grid>
                {importResult.duplicatesSkipped > 0 && (
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1" color="warning.main">
                      <strong>Duplicates Skipped:</strong> {importResult.duplicatesSkipped}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Category Breakdown:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(importResult.summary.categoryBreakdown).map(([cat, count]) => (
                  <Chip
                    key={cat}
                    label={`${cat}: ${count}`}
                    sx={{
                      backgroundColor: CATEGORY_COLORS[cat] || CATEGORY_COLORS['UNKNOWN'],
                      color: 'white'
                    }}
                  />
                ))}
              </Box>
            </Paper>
          )}

    </Box>
  );

  // Session expired on any authenticated request → full-screen re-login state
  // (same pattern as the Activity Monitor kiosk). The stored token was already
  // cleared by pulseRequest.
  if (authExpired) {
    return (
      <Container maxWidth="sm" sx={{ mt: 10, textAlign: 'center' }}>
        <Paper sx={{ p: 5 }}>
          <PersonIcon sx={{ fontSize: 64, color: '#f57c00', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            You need to sign in again
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            The login session has expired. Please sign in again — everything will work right after.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigate('/login')}>
            Go to Sign In
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }} onPointerDownCapture={() => { if (kioskMode && kioskWorker) kioskTouch(); }}>
      {/* ASSOCIATE KIOSK (auto-assignment ON): no OBD list — a count, big name
          tiles, then the verified associate's own OBD with the store number
          dominant. Managers/admins keep the full page below. */}
      {kioskMode && (() => {
        const openObds = obds.filter(o => o.status !== 'Completed');
        const catCount = kioskGroup
          ? (kioskGroup.is_general ? openObds.length
            : openObds.filter(o => (kioskGroup.categories || []).includes(o.category_subset || o.category)).length)
          : null;
        const cur = kioskWorker
          ? obds.find(o => o.assigned_worker === kioskWorker.name && ['In Progress', 'Paused'].includes(o.status))
          : null;
        const tileName = (n) => {
          const parts = String(n || '').trim().split(/\s+/);
          return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0] || '?';
        };
        const rosterSorted = [...kioskRoster]
          .filter(r => r.worker_name)
          .sort((a, b) => a.worker_name.localeCompare(b.worker_name))
          .filter(r => !kioskSearch || r.worker_name.toLowerCase().includes(kioskSearch.toLowerCase()));
        const elapsed = cur && cur.start_time ? Math.max(0, Math.floor((kioskNow - new Date(cur.start_time).getTime()) / 1000)) : 0;
        const fmtEl = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
        return (
          <Box sx={{ pb: 3 }}>
            <Typography sx={{ fontSize: 30, fontWeight: 700, textAlign: 'center', mb: 2 }}>
              {openObds.length} OBDs open{kioskWorker && catCount != null ? ` · ${catCount} in your category` : ''}
            </Typography>
            {!kioskWorker ? (
              <>
                <Typography sx={{ fontSize: 20, color: '#667085', textAlign: 'center', mb: 2 }}>
                  Tap your name to sign on
                </Typography>
                {kioskRoster.length > 20 && (
                  <TextField
                    fullWidth placeholder="Search your name…" value={kioskSearch}
                    onChange={(e) => setKioskSearch(e.target.value)} sx={{ mb: 2, maxWidth: 480, mx: 'auto', display: 'block' }}
                  />
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
                  {rosterSorted.map(r => (
                    <Button
                      key={r.employee_id}
                      variant="outlined"
                      onClick={() => kioskEnterFor(r)}
                      sx={{ minHeight: 96, fontSize: 26, fontWeight: 700, textTransform: 'none', borderWidth: 2 }}
                    >
                      {tileName(r.worker_name)}
                    </Button>
                  ))}
                </Box>
                {rosterSorted.length === 0 && (
                  <Typography sx={{ textAlign: 'center', color: '#667085', fontSize: 20, py: 4 }}>
                    No one is checked in — check in on the Activity Monitor first.
                  </Typography>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{kioskWorker.name}</Typography>
                  <Button size="large" sx={{ minHeight: 64, fontSize: 18 }} onClick={() => { setKioskWorker(null); setKioskGroup(null); }}>
                    Not you? Back
                  </Button>
                </Box>
                {cur ? (
                  <Box sx={{ border: '1px solid #EAECF0', borderRadius: 3, p: 3, boxShadow: '0 1px 2px rgba(16,24,40,0.05)' }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#667085', letterSpacing: 1 }}>STORE</Typography>
                    <Typography sx={{ fontSize: 112, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>
                      {cur.ship_to_code || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: 36, fontWeight: 700, color: '#3E63DD', lineHeight: 1.1 }}>
                      {cur.category_subset || cur.category}
                    </Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 600, mt: 1 }}>OBD {cur.obd_number}</Typography>
                    <Typography sx={{ fontSize: 18, color: '#344054' }}>
                      {cur.departure_time ? `Departs ${cur.departure_time} · ` : ''}{cur.total_lines} lines · {cur.status === 'Paused' ? 'Paused' : `picking ${fmtEl(elapsed)}`}
                    </Typography>
                    {toteConfig && (() => {
                      const totes = toteEstimateFor(cur.total_items, cur.category_subset || cur.category, toteConfig);
                      return totes ? (
                        <ToteEstimate totes={totes} carts={toteCartsNeeded(totes, toteConfig)} fontSize={40} iconSize={44} align="center" mt={2} />
                      ) : null;
                    })()}
                    <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'center' }}>
                      <Button
                        variant="contained" color="success"
                        sx={{ minHeight: 72, fontSize: 24, px: 6, fontWeight: 700 }}
                        onClick={async () => {
                          kioskTouch();
                          const fb = await loadVerifyFeedback('complete', cur.id);
                          await executeCompleteOBD(cur.id, kioskWorker.employee_id, fb);
                        }}
                      >
                        Complete
                      </Button>
                      {cur.status === 'Paused' && (
                        <Button variant="outlined" sx={{ minHeight: 72, fontSize: 20, px: 4 }} onClick={() => handleResumeOBD(cur.id)}>
                          Resume
                        </Button>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="contained" fullWidth
                    sx={{ minHeight: 110, fontSize: 30, fontWeight: 700, borderRadius: 3 }}
                    onClick={() => { kioskTouch(); executeStartNext(kioskWorker.employee_id); }}
                  >
                    Start my next OBD
                  </Button>
                )}
              </Box>
            )}
          </Box>
        );
      })()}

      <Box sx={{ display: kioskMode ? 'none' : 'block' }}>
      {/* Tabs - Manager View only visible to managers and admins */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
        >
          <Tab label="Associate View" />
          {canAccessManagerFeatures && <Tab label="Manager View" />}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 ? (
        <Box>
          {/* Labor Intelligence - only visible to managers/admins */}
          {canAccessManagerFeatures && renderLaborIntelligence()}

          {/* OBD Number Search - visible to all users */}
          <Box sx={{ mb: 2 }}>
            {isHistoricalView ? (
              /* Historical mode: submit-triggered search across ALL dates */
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  label="Search All History"
                  placeholder="Enter OBD number and press Search or Enter…"
                  value={obdSearch}
                  onChange={(e) => setObdSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleHistorySearch(); }}
                  size="small"
                  fullWidth
                  sx={{ maxWidth: 400 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: obdSearch && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => { setObdSearch(''); setHistorySearchResults(null); }}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleHistorySearch}
                  disabled={!obdSearch.trim() || historySearchLoading}
                  startIcon={historySearchLoading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
                  sx={{ whiteSpace: 'nowrap', height: 40, flexShrink: 0 }}
                >
                  Search
                </Button>
                {historySearchResults !== null && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => { setObdSearch(''); setHistorySearchResults(null); }}
                    sx={{ whiteSpace: 'nowrap', height: 40, flexShrink: 0 }}
                  >
                    Back to range
                  </Button>
                )}
              </Box>
            ) : (
              /* Live mode: filter-as-you-type */
              <TextField
                label="Search OBD Number"
                placeholder="Type or scan OBD number..."
                value={obdSearch}
                onChange={(e) => setObdSearch(e.target.value)}
                size="small"
                fullWidth
                sx={{ maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: obdSearch && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setObdSearch('')}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            )}
            {!isHistoricalView && obdSearch && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Showing {filteredAndSortedOBDs.length} of {obds.length} OBDs matching "{obdSearch}"
              </Typography>
            )}
            {isHistoricalView && historySearchResults !== null && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {historySearchResults.length === 0
                  ? `No OBDs found matching "${obdSearch}" in history`
                  : `Found ${historySearchResults.length} OBD${historySearchResults.length !== 1 ? 's' : ''} matching "${obdSearch}" across all dates`}
              </Typography>
            )}
          </Box>

          {renderWorkerSelection()}
          {renderOBDTable()}
        </Box>
      ) : canAccessManagerFeatures ? (
        renderManagerView()
      ) : null}

      {/* Add Associate Dialog */}
      <Dialog open={addWorkerDialogOpen} onClose={() => {
        setAddWorkerDialogOpen(false);
        setNewWorkerError('');
      }}>
        <DialogTitle>Add New Associate</DialogTitle>
        <DialogContent>
          {newWorkerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {newWorkerError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Associate Name"
            value={newWorkerName}
            onChange={(e) => setNewWorkerName(e.target.value)}
            required
          />
          <TextField
            fullWidth
            margin="dense"
            label="Employee ID"
            value={newWorkerEmployeeId}
            onChange={(e) => setNewWorkerEmployeeId(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddWorker()}
            required
            helperText="Required for associates to sign on to OBDs"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddWorkerDialogOpen(false);
            setNewWorkerError('');
          }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddWorker}>Add Associate</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Labor Plan Dialog */}
      <Dialog open={laborDialogOpen} onClose={() => setLaborDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Labor Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Update the staffing levels and shift start time for today's workload.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                type="number"
                label="Full-Time"
                value={editFtAssociates}
                onChange={(e) => setEditFtAssociates(Math.max(0, parseInt(e.target.value) || 0))}
                inputProps={{ min: 0 }}
                helperText="8 hours each"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                type="number"
                label="Part-Time"
                value={editPtAssociates}
                onChange={(e) => setEditPtAssociates(Math.max(0, parseInt(e.target.value) || 0))}
                inputProps={{ min: 0 }}
                helperText="4 hours each"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                type="time"
                label="Shift Start"
                value={editShiftStartTime}
                onChange={(e) => setEditShiftStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="When shift begins"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Total Labor Hours:</strong> {(editFtAssociates * 8) + (editPtAssociates * 4)} hours
            </Typography>
            <Typography variant="body2" color="textSecondary">
              ({editFtAssociates} FT × 8h + {editPtAssociates} PT × 4h)
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Shift:</strong> {new Date(`2000-01-01T${editShiftStartTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
              {editFtAssociates > 0 && ` → FT until ${new Date(new Date(`2000-01-01T${editShiftStartTime}`).getTime() + 8*60*60*1000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
              {editPtAssociates > 0 && ` • PT until ${new Date(new Date(`2000-01-01T${editShiftStartTime}`).getTime() + 4*60*60*1000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
            </Typography>
            {currentLabor && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Work Required:</strong> {Number(currentLabor.total_work_hours || 0).toFixed(1)} hours
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLaborDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateLabor}>Update Labor Plan</Button>
        </DialogActions>
      </Dialog>

      {/* Pre-Assign Dialog (supervisor assigns an Available OBD to an associate) */}
      <Dialog open={preassignDialogOpen} onClose={() => { setPreassignDialogOpen(false); setPreassignTargetObd(null); }}>
        <DialogTitle>
          {preassignTargetObd?.assigned_worker ? 'Change Pre-Assignment' : 'Assign OBD'} {preassignTargetObd?.obd_number}
        </DialogTitle>
        <DialogContent>
          {preassignTargetObd?.assigned_worker ? (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Currently pre-assigned to: <strong>{preassignTargetObd?.assigned_worker}</strong>
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Pick an associate to assign this OBD to. The OBD stays Available until they Start it.
          </Typography>
          <Autocomplete
            options={workers.filter(w => w.name !== preassignTargetObd?.assigned_worker)}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField {...params} label="Select Associate" variant="outlined" fullWidth autoFocus />
            )}
            onChange={(e, newValue) => {
              if (newValue && preassignTargetObd) {
                handlePreassignOBD(preassignTargetObd.id, newValue.name);
              }
            }}
            sx={{ minWidth: 300 }}
          />
        </DialogContent>
        <DialogActions>
          {preassignTargetObd?.assigned_worker && (
            <Button
              color="error"
              onClick={() => {
                if (preassignTargetObd) {
                  handleUnassignOBD(preassignTargetObd.id);
                  setPreassignDialogOpen(false);
                  setPreassignTargetObd(null);
                }
              }}
            >
              Clear Assignment
            </Button>
          )}
          <Button onClick={() => { setPreassignDialogOpen(false); setPreassignTargetObd(null); }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Assign Dialog (supervisor assigns multiple Available OBDs to one associate) */}
      <Dialog open={bulkAssignDialogOpen} onClose={() => setBulkAssignDialogOpen(false)}>
        <DialogTitle>
          Assign {selectedOBDs.length} OBD{selectedOBDs.length !== 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Pick an associate to assign these OBDs to. They stay Available until the associate Starts them.
          </Typography>
          <Autocomplete
            options={workers}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField {...params} label="Select Associate" variant="outlined" fullWidth autoFocus />
            )}
            onChange={(e, newValue) => {
              if (newValue) {
                handleBulkAssign(newValue.name);
              }
            }}
            sx={{ minWidth: 300 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAssignDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={reassignDialogOpen} onClose={() => { setReassignDialogOpen(false); setReassignTargetObd(null); }}>
        <DialogTitle>Reassign OBD {reassignTargetObd?.obd_number}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Currently assigned to: <strong>{reassignTargetObd?.assigned_worker}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a new associate to take over this assignment. The timer will continue from the original start time.
          </Typography>
          <Autocomplete
            options={workers.filter(w => w.name !== reassignTargetObd?.assigned_worker)}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField {...params} label="Select New Associate" variant="outlined" fullWidth />
            )}
            onChange={(e, newValue) => {
              if (newValue && reassignTargetObd) {
                handleReassignOBD(reassignTargetObd.id, newValue.name);
              }
            }}
            sx={{ minWidth: 300 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReassignDialogOpen(false); setReassignTargetObd(null); }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      </Box>

      {/* Employee ID Verification Dialog (for associates only) */}
      {/* Auto-assignment popup: completion verdict (two tiers) + the next OBD
          with the STORE NUMBER as the dominant element. Gated: verdict only
          when standards feedback is on; whole dialog only in auto mode. */}
      {/* 90s auto-dismiss (timer in the nextObdDialog effect above); tapping
          the backdrop or the X closes early. The kiosk ACTIVE screen behind
          it keeps showing the OBD number, store and category after close. */}
      <Dialog open={!!nextObdDialog} onClose={() => setNextObdDialog(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3, position: 'relative' }}>
          <IconButton
            aria-label="close"
            onClick={() => setNextObdDialog(null)}
            sx={{ position: 'absolute', top: 6, right: 6, color: '#98A2B3' }}
          >
            ✕
          </IconButton>
          {nextObdDialog?.verdict && (() => {
            const v = nextObdDialog.verdict;
            const C = { good: '#22A06B', needs: '#E5484D', not_scored: '#98A2B3' };
            const label = v.tier === 'good' ? 'Good job' : v.tier === 'needs' ? 'Needs improvement' : 'Not scored';
            return (
              <Box sx={{ textAlign: 'center', pb: 2, mb: 2, borderBottom: '1px solid #EAECF0' }}>
                <Typography sx={{ fontSize: 26, fontWeight: 700, color: C[v.tier] }}>{label}</Typography>
                {v.tier !== 'not_scored' ? (
                  <>
                    <Typography sx={{ fontSize: 56, fontWeight: 700, lineHeight: 1.1, color: C[v.tier] }}>{v.pct}%</Typography>
                    <Typography sx={{ fontSize: 18, color: v.line === 'Right on time' ? C[v.tier] : '#344054' }}>
                      {v.line}{v.batch ? ' (batch)' : ''}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 18, color: '#667085' }}>No standard for this one{v.batch ? ' (batch)' : ''}.</Typography>
                )}
              </Box>
            );
          })()}
          {nextObdDialog?.obd ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#667085', letterSpacing: 1 }}>NEXT — STORE</Typography>
              <Typography sx={{ fontSize: 88, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.02em', color: '#101828' }}>
                {nextObdDialog.obd.ship_to || '—'}
              </Typography>
              <Typography sx={{ fontSize: 30, fontWeight: 700, color: '#3E63DD', lineHeight: 1.1 }}>
                {nextObdDialog.obd.category}
              </Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 600, mt: 0.5 }}>
                OBD {nextObdDialog.obd.obd_number}
              </Typography>
              <Typography sx={{ fontSize: 18, color: '#344054' }}>
                {nextObdDialog.obd.departure_at
                  ? `Departs ${new Date(nextObdDialog.obd.departure_at).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} ET · `
                  : ''}{nextObdDialog.obd.lines} lines
              </Typography>
              <Typography sx={{ fontSize: 16, color: '#667085' }}>
                {nextObdDialog.obd.units} units · {nextObdDialog.obd.platform}
              </Typography>
              {nextObdDialog.expectedSeconds && (
                <Typography sx={{ fontSize: 16, mt: 1, color: '#667085' }}>
                  Standard time: {fmtDur(nextObdDialog.expectedSeconds)} · Expected done by {doneBy(nextObdDialog.expectedSeconds)}
                </Typography>
              )}
              {toteConfig && (() => {
                const totes = toteEstimateFor(nextObdDialog.obd.units, nextObdDialog.obd.category, toteConfig);
                return totes ? (
                  <ToteEstimate totes={totes} carts={toteCartsNeeded(totes, toteConfig)} fontSize={20} iconSize={24} align="center" />
                ) : null;
              })()}
            </Box>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center' }}>{nextObdDialog?.message}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {/* "Taking a break" removed — breaks go through the Activity
              Monitor, which releases (<150s) or auto-pauses the OBD */}
          <Button variant="contained" onClick={() => setNextObdDialog(null)} sx={{ minHeight: 48, px: 4 }}>Got it</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={verifyDialogOpen} onClose={handleCancelVerify}>
        <DialogTitle>
          Enter Employee ID for {selectedWorker?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Please enter your Employee ID to confirm this action.
          </Typography>
          {/* Standards feedback — neutral wording and plain styling by design:
              no red/green judgment in this dialog. Absent whenever the toggle
              is off, the category has no standard, or the lookup failed. */}
          {verifyFeedback && pendingAction && (
            (verifyFeedback.mode === 'start' && (pendingAction.type === 'claim' || pendingAction.type === 'multiClaim')) ||
            (verifyFeedback.mode === 'complete' && pendingAction.type === 'complete')
          ) && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {verifyFeedback.mode === 'start' ? (
                <Typography variant="body2">
                  Standard time{verifyFeedback.batch ? ' (batch)' : ''}: {fmtDur(verifyFeedback.expectedSeconds)} · Expected done by {doneBy(verifyFeedback.expectedSeconds)}
                </Typography>
              ) : (
                <>
                  <Typography variant="body2">
                    Standard {fmtDur(verifyFeedback.expectedSeconds)} · Actual {fmtDur(verifyFeedback.actualSeconds)}{verifyFeedback.batch ? ' (batch)' : ''}
                  </Typography>
                  <Typography variant="body2">
                    {Math.round(verifyFeedback.actualSeconds / 60) === 0
                      ? 'Not scored (under 30 seconds)'
                      : `${Math.round(100 * verifyFeedback.expectedSeconds / verifyFeedback.actualSeconds)}% to standard on this OBD`}
                  </Typography>
                  {verifyFeedback.dayPct != null && (
                    <Typography variant="body2">Today so far: {verifyFeedback.dayPct}% to standard</Typography>
                  )}
                </>
              )}
            </Box>
          )}
          {/* The OBD's CATEGORY, prominent next to the store — associates
              always see what they are picking (no separate loan messaging) */}
          {pendingAction && (pendingAction.type === 'claim' || pendingAction.type === 'multiClaim') && (() => {
            const ids = pendingAction.type === 'claim' ? [pendingAction.obdId] : (pendingAction.obdIds || []);
            const first = obds.find(o => o.id === ids[0]);
            if (!first) return null;
            return (
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 26, fontWeight: 700, color: '#3E63DD', lineHeight: 1.1 }}>
                  {first.category_subset || first.category}
                </Typography>
                <Typography sx={{ fontSize: 15, color: '#667085' }}>
                  Store {first.store_display_name || first.ship_to_code || '—'}{ids.length > 1 ? ` · ${ids.length} OBDs` : ''}
                </Typography>
              </Box>
            );
          })()}
          {/* Tote estimate at start — batch shows the summed total */}
          {toteConfig && pendingAction && (pendingAction.type === 'claim' || pendingAction.type === 'multiClaim') && (() => {
            const ids = pendingAction.type === 'claim' ? [pendingAction.obdId] : (pendingAction.obdIds || []);
            const rows = ids.map(id => obds.find(o => o.id === id)).filter(Boolean)
              .map(o => ({ units: o.total_items, category: o.category_subset || o.category }));
            const totes = toteBatchTotal(rows, toteConfig);
            if (!totes) return null;
            return (
              <Box sx={{ mb: 2 }}>
                <ToteEstimate totes={totes} carts={toteCartsNeeded(totes, toteConfig)} fontSize={18} iconSize={22} mt={0} />
              </Box>
            );
          })()}
          {verifyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {verifyError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Employee ID"
            value={verifyEmployeeId}
            onChange={(e) => setVerifyEmployeeId(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleVerifyAndExecute();
              }
            }}
            disabled={verifyLoading}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelVerify} disabled={verifyLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleVerifyAndExecute}
            variant="contained"
            disabled={verifyLoading || !verifyEmployeeId.trim()}
          >
            {verifyLoading ? <CircularProgress size={24} /> : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check-in SOFT nag (Change 6): reminder only — the pick already went through.
          Dismissible; re-appears on each OBD start while still not checked in. */}
      <Dialog open={clockNag.open} onClose={() => setClockNag({ open: false, message: '' })}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          ⏰ Reminder: Please Check In
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {clockNag.message}
          </Alert>
          <Typography variant="body2" color="textSecondary">
            Your OBD has started — this is just a reminder. Your time is tracked separately, so please check in when you get a moment.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClockNag({ open: false, message: '' })}>Dismiss</Button>
          <Button
            variant="contained"
            startIcon={<AccessTimeIcon />}
            onClick={() => { setClockNag({ open: false, message: '' }); navigate('/clock-in'); }}
          >
            Go to Activity Monitor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto-Picking corrective warning: their state was fixed for them.
          Dismissible only — the pick already succeeded; nothing is blocked. */}
      <Dialog open={autoPickWarn.open} onClose={() => setAutoPickWarn({ open: false, message: '' })}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          ⚠️ Auto Checked In — Reminder
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {autoPickWarn.message}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setAutoPickWarn({ open: false, message: '' })}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>

      {/* OBD Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
              OBD {obdDetails?.obd_number}
            </Typography>
            {obdDetails && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                {getPlatformChip(obdDetails.platform)}
                {getCategoryChip(obdDetails.category_subset || obdDetails.category)}
                {getStatusChip(obdDetails.status, obdDetails.auto_completed)}
              </Box>
            )}
          </Box>
          <Button onClick={handleCloseDetails} color="inherit">Close</Button>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : obdDetails ? (
            <Box>
              {/* OBD Header Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="textSecondary">Ship-To</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {obdDetails.store_display_name || obdDetails.ship_to_code}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="textSecondary">Total Items</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{obdDetails.total_items}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="textSecondary">Total Lines</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{obdDetails.total_lines}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="textSecondary">Assigned Associate</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {obdDetails.assigned_worker || '-'}
                  </Typography>
                </Grid>
                {/* Timing — Duration is active pick time (pause subtracted), Elapsed is
                    gross start→finish wall-clock, Pause is accumulated paused minutes */}
                {obdDetails.status === 'Completed' && !obdDetails.auto_completed && obdDetails.start_time && obdDetails.end_time && (
                  <>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Duration (active)</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {calculateDuration(obdDetails.start_time, obdDetails.end_time, obdDetails.total_paused_seconds)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Elapsed (start to finish)</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {calculateDuration(obdDetails.start_time, obdDetails.end_time, 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Pause</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {Math.round((obdDetails.total_paused_seconds || 0) / 60)}m
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* Line Items Table */}
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Line Items ({obdDetails.lines?.length || 0})
              </Typography>
              {obdDetails.lines && obdDetails.lines.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Material</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {obdDetails.lines.map((line, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{line.material}</TableCell>
                          <TableCell>{line.description || '-'}</TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 500 }}>{line.quantity}</TableCell>
                          <TableCell>
                            {line.department ? (
                              <Chip
                                label={line.department}
                                size="small"
                                sx={{
                                  backgroundColor: CATEGORY_COLORS[line.department] || '#9e9e9e',
                                  color: 'white'
                                }}
                              />
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No line items found for this OBD.</Alert>
              )}
            </Box>
          ) : (
            <Alert severity="error">Failed to load OBD details.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Associate Report Modal */}
      <Dialog
        open={associateModalOpen}
        onClose={() => setAssociateModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
              {selectedAssociateForReport?.name} - Productivity Report
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={associateReportPeriod}
            exclusive
            onChange={handleAssociateReportPeriodChange}
            size="small"
          >
            <ToggleButton value="day">Today</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
          </ToggleButtonGroup>
        </DialogTitle>
        <DialogContent dividers>
          {loadingAssociateStats ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : associateStats ? (
            <Box>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e3f2fd' }}>
                    <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                      {associateStats.summary.obds_completed}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">OBDs Completed</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e9' }}>
                    <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                      {associateStats.summary.total_lines}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Total Lines</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#f3e5f5' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
                      {associateStats.summary.lines_per_hour}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Lines/Hour</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#fff3e0' }}>
                    <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                      {Math.round(associateStats.summary.total_minutes)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Minutes Worked</Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* Start-to-Finish (wall-clock) cards — shown alongside, never replacing, active-time stats */}
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={4}>
                  <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#eceff1' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#37474f' }}>
                      {associateStats.summary.avg_elapsed_minutes}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Avg Elapsed (min)</Typography>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#eceff1' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#37474f' }}>
                      {associateStats.summary.avg_pause_minutes}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Avg Pause (min)</Typography>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#eceff1' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#37474f' }}>
                      {associateStats.summary.pct_obds_paused}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">% OBDs Paused</Typography>
                  </Card>
                </Grid>
              </Grid>
              {associateStats.summary.excluded_outliers > 0 && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                  {associateStats.summary.excluded_outliers} outlier{associateStats.summary.excluded_outliers === 1 ? '' : 's'} excluded
                  from Elapsed/Pause averages (elapsed over 8h or multi-assignment time splits).
                </Typography>
              )}

              {/* Category Breakdown Table */}
              {associateStats.categoryBreakdown && associateStats.categoryBreakdown.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Productivity by Category
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>OBDs</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Lines</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Minutes</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Lines/Hour</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {associateStats.categoryBreakdown.map((cat, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: CATEGORY_COLORS[cat.category] || '#9e9e9e'
                                  }}
                                />
                                {cat.category}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{cat.obds}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{cat.lines}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{Math.round(cat.minutes)}</TableCell>
                            <TableCell sx={{ textAlign: 'center', fontWeight: 600 }}>{cat.lines_per_hour}</TableCell>
                          </TableRow>
                        ))}
                        {/* Total Row */}
                        <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>
                            {associateStats.categoryBreakdown.reduce((sum, cat) => sum + cat.obds, 0)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>
                            {associateStats.categoryBreakdown.reduce((sum, cat) => sum + cat.lines, 0)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>
                            {Math.round(associateStats.categoryBreakdown.reduce((sum, cat) => sum + cat.minutes, 0))}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>
                            {associateStats.summary.lines_per_hour}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Daily Breakdown Table */}
              {associateStats.daily && associateStats.daily.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Daily Breakdown
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>OBDs</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Lines</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Units</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Minutes</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Lines/Hour</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {associateStats.daily.map((day, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{new Date(day.date).toLocaleDateString()}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{day.obds}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{day.lines}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{day.units}</TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{Math.round(day.minutes)}</TableCell>
                            <TableCell sx={{ textAlign: 'center', fontWeight: 600 }}>{day.lines_per_hour}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {associateStats.summary.obds_completed === 0 && (
                <Alert severity="info">
                  No completed OBDs found for this period.
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="error">Failed to load associate stats.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssociateModalOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Labor Analysis Results Dialog */}
      <Dialog open={laborAnalysisDialogOpen} onClose={() => setLaborAnalysisDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: laborAnalysis?.laborAdequate ? 'success.main' : 'warning.main', color: 'white' }}>
          Import Complete - Labor Analysis
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {laborAnalysis && (
            <>
              {/* Success summary */}
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6">
                  {importResult?.summary?.totalObds || 0} OBDs Imported Successfully
                </Typography>
                <Typography variant="body1">
                  {importResult?.summary?.totalLines || 0} lines • {importResult?.summary?.totalItems || 0} items
                </Typography>
                {importResult?.duplicatesSkipped > 0 && (
                  <Typography variant="caption" color="warning.main">
                    {importResult.duplicatesSkipped} duplicates skipped
                  </Typography>
                )}
              </Alert>

              {/* Labor analysis */}
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Labor Analysis
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Paper elevation={3} sx={{ p: 3, bgcolor: '#e3f2fd', textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">
                      Available Labor
                    </Typography>
                    <Typography variant="h3" color="primary" sx={{ my: 1, fontWeight: 'bold' }}>
                      {laborAnalysis.totalLaborHours}h
                    </Typography>
                    <Typography variant="body1">
                      {laborAnalysis.ftAssociates} Full-Time (8h each)
                    </Typography>
                    <Typography variant="body1">
                      {laborAnalysis.ptAssociates} Part-Time (4h each)
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper elevation={3} sx={{ p: 3, bgcolor: '#fff3e0', textAlign: 'center' }}>
                    <Typography variant="overline" color="text.secondary">
                      Work Required
                    </Typography>
                    <Typography variant="h3" color="warning.dark" sx={{ my: 1, fontWeight: 'bold' }}>
                      {laborAnalysis.totalWorkHours}h
                    </Typography>
                    <Typography variant="body1">
                      {importResult?.summary?.totalLines || 0} lines @ 45 LPH
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Standard: 45 lines per hour
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Verdict */}
              {laborAnalysis.laborAdequate ? (
                <Alert severity="success" sx={{ fontSize: '1.1rem' }}>
                  <Typography variant="h6" gutterBottom>
                    You Have Adequate Staffing!
                  </Typography>
                  <Typography variant="body1">
                    You have <strong>{Number(laborAnalysis.hoursSurplus || (laborAnalysis.totalLaborHours - laborAnalysis.totalWorkHours) || 0).toFixed(1)} hours</strong> of extra capacity.
                  </Typography>
                  {(laborAnalysis.associatesSurplus?.ft > 0 || laborAnalysis.hoursSurplus > 8) && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      That's approximately <strong>{laborAnalysis.associatesSurplus?.ft || Math.floor((laborAnalysis.totalLaborHours - laborAnalysis.totalWorkHours) / 8)} extra full-time associate{(laborAnalysis.associatesSurplus?.ft || Math.floor((laborAnalysis.totalLaborHours - laborAnalysis.totalWorkHours) / 8)) !== 1 ? 's' : ''}</strong> worth of capacity.
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Alert severity="error" sx={{ fontSize: '1.1rem' }}>
                  <Typography variant="h6" gutterBottom>
                    Insufficient Staffing
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    You need <strong>{laborAnalysis.hoursDeficit} more hours</strong> of labor to complete these OBDs.
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Recommended:
                  </Typography>
                  <Typography variant="body1">
                    • Add <strong>{laborAnalysis.associatesNeeded?.ft || Math.ceil(laborAnalysis.hoursDeficit / 8)} full-time associate{(laborAnalysis.associatesNeeded?.ft || Math.ceil(laborAnalysis.hoursDeficit / 8)) !== 1 ? 's' : ''}</strong> (8 hours each)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2, my: 0.5 }}>
                    OR
                  </Typography>
                  <Typography variant="body1">
                    • Add <strong>{laborAnalysis.associatesNeeded?.pt || Math.ceil(laborAnalysis.hoursDeficit / 4)} part-time associate{(laborAnalysis.associatesNeeded?.pt || Math.ceil(laborAnalysis.hoursDeficit / 4)) !== 1 ? 's' : ''}</strong> (4 hours each)
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLaborAnalysisDialogOpen(false)} variant="contained" size="large">Got It</Button>
        </DialogActions>
      </Dialog>

      {/* Set View for Associate Dialog (Manager only) */}
      <Dialog open={setViewDialogOpen} onClose={() => setSetViewDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SaveIcon color="primary" />
            Set View for Associate
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Save your current filter settings as the default view for an associate.
            When they log in, these filters will be automatically applied.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Filters:</Typography>
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {platformFilter.length > 0 && (
              <Chip size="small" label={`${platformExclude ? 'NOT ' : ''}Platform: ${platformFilter.join(', ')}`} color={platformExclude ? "error" : "primary"} />
            )}
            {statusFilter.length > 0 && (
              <Chip size="small" label={`${statusExclude ? 'NOT ' : ''}Status: ${statusFilter.join(', ')}`} color={statusExclude ? "error" : "primary"} />
            )}
            {categoryFilter.length > 0 && (
              <Chip size="small" label={`${categoryExclude ? 'NOT ' : ''}Category: ${categoryFilter.join(', ')}`} color={categoryExclude ? "error" : "primary"} />
            )}
            {shipToFilter.length > 0 && (
              <Chip size="small" label={`${shipToExclude ? 'NOT ' : ''}Ship-To: ${shipToFilter.join(', ')}`} color={shipToExclude ? "error" : "primary"} />
            )}
            {platformFilter.length === 0 && statusFilter.length === 0 && categoryFilter.length === 0 && shipToFilter.length === 0 && (
              <Chip size="small" label="No filters (show all)" variant="outlined" />
            )}
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Associate:</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {associatesList.filter(a => a.role === 'associate').map((associate) => (
              <Button
                key={associate.username}
                variant="outlined"
                fullWidth
                startIcon={<PersonIcon />}
                onClick={async () => {
                  const success = await saveViewSettings(associate.username);
                  if (success) {
                    setSetViewDialogOpen(false);
                  }
                }}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              >
                {associate.fullName || associate.username}
              </Button>
            ))}
            {associatesList.filter(a => a.role === 'associate').length === 0 && (
              <Alert severity="info">No associates found</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetViewDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* View As Associate Dialog (Manager only) */}
      <Dialog open={viewAsDialogOpen} onClose={() => setViewAsDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="primary" />
            View As Associate
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Load an associate's saved view settings to see the page as they would see it.
          </Typography>
          {viewingAsAssociate && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Currently viewing as: <strong>{viewingAsAssociate}</strong>
            </Alert>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Associate:</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {viewingAsAssociate && (
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={<ClearIcon />}
                onClick={() => {
                  setViewingAsAssociate(null);
                  // Reset to default filters (all arrays now)
                  setPlatformFilter([]);
                  setStatusFilter([]);
                  setCategoryFilter([]);
                  setShipToFilter([]);
                  setPlatformExclude(false);
                  setStatusExclude(false);
                  setCategoryExclude(false);
                  setShipToExclude(false);
                  setViewAsDialogOpen(false);
                  setSnackbar({
                    open: true,
                    message: 'Returned to your own view',
                    severity: 'info'
                  });
                }}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 1 }}
              >
                Clear - Return to My View
              </Button>
            )}
            {associatesList.filter(a => a.role === 'associate').map((associate) => (
              <Button
                key={associate.username}
                variant={viewingAsAssociate === associate.username ? "contained" : "outlined"}
                fullWidth
                startIcon={<PersonIcon />}
                onClick={async () => {
                  const success = await fetchViewSettings(associate.username);
                  if (success) {
                    setViewingAsAssociate(associate.username);
                    setViewAsDialogOpen(false);
                    setSnackbar({
                      open: true,
                      message: `Now viewing as ${associate.fullName || associate.username}`,
                      severity: 'success'
                    });
                  } else {
                    setSnackbar({
                      open: true,
                      message: `No saved view settings for ${associate.fullName || associate.username}`,
                      severity: 'warning'
                    });
                  }
                }}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              >
                {associate.fullName || associate.username}
              </Button>
            ))}
            {associatesList.filter(a => a.role === 'associate').length === 0 && (
              <Alert severity="info">No associates found</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewAsDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Reset All Views Confirmation Dialog */}
      <Dialog open={resetViewsDialogOpen} onClose={() => setResetViewsDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          Reset All Associate Views?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will clear all custom filters for all associates.
          </Alert>
          <Typography variant="body2">
            {allViewSettings.length} associate{allViewSettings.length !== 1 ? 's' : ''} will be affected:
          </Typography>
          <Box sx={{ mt: 1, pl: 2 }}>
            {allViewSettings.map(vs => (
              <Typography key={vs.username} variant="body2" color="text.secondary">
                • {vs.full_name || vs.username}
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            They will see the default unfiltered view when they next log in. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetViewsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={resetAllViewSettings}>
            Reset All Views
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Settings Detail Dialog */}
      <Dialog open={viewSettingsDetailOpen} onClose={() => setViewSettingsDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Associates with Custom Filters
        </DialogTitle>
        <DialogContent>
          {allViewSettings.length === 0 ? (
            <Alert severity="info">No associates have custom filters set.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {allViewSettings.map(vs => {
                const filters = typeof vs.filters === 'string' ? JSON.parse(vs.filters) : vs.filters;
                const filterDescriptions = [];

                if (filters.platformFilter?.length > 0) {
                  filterDescriptions.push(`${filters.platformExclude ? 'NOT ' : ''}Platform: ${filters.platformFilter.join(', ')}`);
                }
                if (filters.statusFilter?.length > 0) {
                  filterDescriptions.push(`${filters.statusExclude ? 'NOT ' : ''}Status: ${filters.statusFilter.join(', ')}`);
                }
                if (filters.categoryFilter?.length > 0) {
                  filterDescriptions.push(`${filters.categoryExclude ? 'NOT ' : ''}Category: ${filters.categoryFilter.join(', ')}`);
                }
                if (filters.shipToFilter?.length > 0) {
                  filterDescriptions.push(`${filters.shipToExclude ? 'NOT ' : ''}Ship-To: ${filters.shipToFilter.join(', ')}`);
                }

                return (
                  <Paper key={vs.username} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {vs.full_name || vs.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Set by {vs.set_by} on {new Date(vs.updated_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          fetchViewSettings(vs.username);
                          setViewingAsAssociate(vs.username);
                          setViewSettingsDetailOpen(false);
                          setSnackbar({
                            open: true,
                            message: `Now viewing as ${vs.full_name || vs.username}`,
                            severity: 'success'
                          });
                        }}
                      >
                        View As
                      </Button>
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {filterDescriptions.map((desc, idx) => (
                        <Chip
                          key={idx}
                          size="small"
                          label={desc}
                          color={desc.startsWith('NOT ') ? 'error' : 'primary'}
                        />
                      ))}
                      {filterDescriptions.length === 0 && (
                        <Typography variant="body2" color="text.secondary">No active filters</Typography>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSettingsDetailOpen(false)}>Close</Button>
          {allViewSettings.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setViewSettingsDetailOpen(false);
                setResetViewsDialogOpen(true);
              }}
            >
              Reset All Views
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Reset OBD Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => { setResetDialogOpen(false); setResetTargetObd(null); }}>
        <DialogTitle sx={{ color: 'warning.main' }}>
          Reset OBD to Available?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to reset <strong>OBD {resetTargetObd?.obd_number}</strong> back to Available?
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This will clear all assignment and timing data for this OBD.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResetDialogOpen(false); setResetTargetObd(null); }}>Cancel</Button>
          <Button onClick={handleResetOBD} color="warning" variant="contained" startIcon={<RestartAltIcon />}>
            Reset OBD
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Selected OBDs?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete <strong>{selectedOBDs.length} OBD{selectedOBDs.length !== 1 ? 's' : ''}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          pt: 2,
          pb: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Powered by
        </Typography>
        <img
          src={ClarisLogo}
          alt="Claris AI"
          style={{ height: 40, opacity: 0.7 }}
        />
      </Box>
    </Container>
    </LocalizationProvider>
  );
};

export default OBDSignOn;
// deploy fix
